import { cartAdd } from '@theme/cart-add';
import { shopifyQuery } from '@theme/kit-api';
import { containsBlacklistedWord } from '@theme/kit-blacklist';
import { applyMaxLength, parseLetterLimit } from '@theme/kit-input-limit';
import { CartAddEvent } from '@theme/events';

// ============================================================================
// [FICT-DEBUG] Temporary instrumentation to debug fictitious-product matching (PDP)
// ----------------------------------------------------------------------------
// Logs, step by step, how the fictitious product is resolved: the computed
// customization type, the filters sent to the Storefront API, the returned
// candidates, and a field-by-field (OK/FAIL) matching table.
//
//  • Enable:  add ?kit_debug to the URL, or run
//             localStorage.setItem('KIT_DEBUG', '1') and reload
//             (a plain window.KIT_DEBUG is lost on reload, since the flag is read at module load).
//  • Disable: localStorage.removeItem('KIT_DEBUG')
//  • Remove this whole block and its fictLog/fictGroup/fictTable calls
//    once the configuration issue has been located.
// ============================================================================
const KIT_DEBUG = (() => {
  try {
    return (
      /** @type {any} */ (window).KIT_DEBUG === true ||
      new URLSearchParams(window.location.search).has('kit_debug') ||
      window.localStorage.getItem('KIT_DEBUG') === '1'
    );
  } catch {
    return false;
  }
})();
function fictLog(/** @type {...any} */ ...args) {
  if (KIT_DEBUG) console.log('%c[FICT]', 'color:#a50044;font-weight:bold', ...args);
}
function fictWarn(/** @type {...any} */ ...args) {
  if (KIT_DEBUG) console.warn('%c[FICT]', 'color:#a50044;font-weight:bold', ...args);
}
function fictGroup(/** @type {string} */ label) {
  if (KIT_DEBUG) console.group('%c[FICT] ' + label, 'color:#004d98;font-weight:bold');
}
function fictGroupEnd() {
  if (KIT_DEBUG) console.groupEnd();
}
function fictTable(/** @type {any[]} */ rows) {
  if (KIT_DEBUG && rows && rows.length) console.table(rows);
}

// ============================================================================
// [SHORTS-DEBUG] Temporary instrumentation for the shorts customization, under
// the same window.KIT_DEBUG switch. Tags:
//  • SCOPE   — what can be printed (customization_scope / service) and why.
//  • PLAYERS — why the players display renders or not.
//  • CUSTOM  — how a typed name/number reaches its fictitious product.
// Remove this block, its shortsLog/shortsWarn/shortsGroup calls and
// snippets/kit-debug-data.liquid once the shorts customization is settled.
// ============================================================================
// Every message starts with "[SHORTS-DEBUG] <tag>" so the console can be
// filtered by either.
const SHORTS_STYLE = 'color:#fff;background:#a50044;font-weight:bold;padding:0 4px';
function shortsLog(/** @type {string} */ tag, /** @type {...any} */ ...args) {
  if (KIT_DEBUG) console.log(`%c[SHORTS-DEBUG] ${tag}`, SHORTS_STYLE, ...args);
}
function shortsWarn(/** @type {string} */ tag, /** @type {...any} */ ...args) {
  if (KIT_DEBUG) console.warn(`%c[SHORTS-DEBUG] ${tag}`, SHORTS_STYLE, ...args);
}
function shortsGroup(/** @type {string} */ tag, /** @type {string} */ label) {
  if (KIT_DEBUG) console.group(`%c[SHORTS-DEBUG] ${tag}%c ${label}`, SHORTS_STYLE, 'font-weight:bold');
}
function shortsGroupEnd() {
  if (KIT_DEBUG) console.groupEnd();
}

// Prints what Liquid decided (snippets/kit-debug-data) next to what ended up in
// the DOM, once per page load.
function dumpShortsDebug() {
  if (!KIT_DEBUG) return;

  /** @type {any} */
  let data = null;
  try {
    data = JSON.parse(document.getElementById('kit-debug-data')?.textContent || 'null');
  } catch (err) {
    shortsWarn('DEBUG', 'kit-debug-data is not valid JSON:', err);
  }
  if (!data) {
    shortsWarn('DEBUG', 'No #kit-debug-data on the page (customization block not rendered?)');
    return;
  }

  // ── SCOPE ──
  const block = /** @type {HTMLElement | null} */ (document.querySelector('.kit-customization'));
  const scope = data.scope;
  shortsGroup('SCOPE', 'What can be printed');
  shortsLog('SCOPE', 'Liquid input:', scope);
  shortsLog('SCOPE', 'Liquid result → scope_name:', scope.scope_name, '| scope_number:', scope.scope_number);
  shortsLog('SCOPE', 'DOM → .kit-customization data-scope-name:', block?.dataset.scopeName ?? '(no block)',
    '| data-scope-number:', block?.dataset.scopeNumber ?? '(no block)',
    '| #kit-name rendered:', !!document.getElementById('kit-name'),
    '| #kit-number rendered:', !!document.getElementById('kit-number'));
  // Why Liquid does or does not see a service variant.
  for (const [where, mf, count] of [
    ['base product', scope.service_metafield_on_base_product, scope.service_variants_on_base_product],
    ['page product', scope.service_metafield_on_product, scope.service_variants_on_product],
  ]) {
    const raw = String(mf?.raw || '');
    const gids = raw.match(/gid:\/\/shopify\/ProductVariant\/\d+/g) || [];
    shortsLog('SCOPE', `customization.customization_service on the ${where} → type: ${mf?.type || '(none)'} | raw:`, raw || '(empty)',
      `| GIDs in raw: ${gids.length} | variants Liquid resolved: ${count}`);
    if (!raw) {
      shortsWarn('SCOPE', `  ${where}: the metafield is EMPTY for Liquid → wrong namespace/key, not set on this product, or not exposed to the storefront.`);
    } else if (gids.length && !count) {
      shortsWarn('SCOPE', `  ${where}: the metafield holds variant GIDs but Liquid resolves none → the referenced product is most likely not available on the Online Store channel / current market.`);
    }
  }

  // The scope comes from the service variant's "Customization scope" option;
  // only products without a service variant fall back to the metafield.
  const serviceOptions = scope.service_variant?.options || {};
  const serviceScope = Object.entries(serviceOptions).find(([name]) => /scope/i.test(name))?.[1];
  if (serviceScope !== undefined) {
    shortsLog('SCOPE', `source: service variant option = "${serviceScope}"`);
  } else if (scope.service_variant) {
    shortsWarn('SCOPE', 'source: service variant, but it has no option whose name contains "scope" → both Name and Number allowed. Options:', serviceOptions);
  } else if (scope.customization_scope_on_base_product.length) {
    shortsLog('SCOPE', 'source: no service variant → customization.customization_scope fallback:', scope.customization_scope_on_base_product);
  } else {
    shortsWarn('SCOPE', 'source: no service variant and no customization.customization_scope → both Name and Number allowed.');
  }
  shortsGroupEnd();

  // ── PLAYERS ──
  const players = data.players;
  const select = /** @type {HTMLSelectElement | null} */ (document.getElementById('kit-player'));
  shortsGroup('PLAYERS', 'Why the players display renders or not');
  shortsLog('PLAYERS', 'hide_players_display:', players.hide_players_display, '| has_players:', players.has_players,
    '| branch:', players.branch, '| default competition:', players.default_competition, '| competition gender:', players.competition_gender);
  shortsLog('PLAYERS', 'Values each players_display must equal (read from `product`):', players.expected);
  shortsLog('PLAYERS', 'Same values on the base product, for comparison:', players.base_product_values_for_comparison);
  shortsLog('PLAYERS', 'players_display candidates (table below):');
  console.table(players.candidates.map((/** @type {any} */ { backs_detail, ...rest }) => rest));
  const fullMatches = players.candidates.filter((/** @type {any} */ c) => c.match_season && c.match_activity && c.match_role);
  shortsLog('PLAYERS', `players_display matching season + activity + role: ${fullMatches.length}`,
    fullMatches.map((/** @type {any} */ c) => c.handle));
  // Each matching display's backs. Only backs with a name become an option
  // (the `{% if back.name %}` guard in kit-customization).
  fullMatches.forEach((/** @type {any} */ c) => {
    const rendered = c.backs_detail.filter((/** @type {any} */ b) => b.rendered_as_option).length;
    (rendered < c.backs_detail.length ? shortsWarn : shortsLog)('PLAYERS',
      `backs of "${c.handle}": ${c.backs_detail.length} | rendered as options: ${rendered} (table below)`);
    console.table(c.backs_detail);
  });
  if (select) {
    shortsLog('PLAYERS', 'Options actually in #kit-player (table below):');
    console.table(Array.from(select.options).map((o) => ({
      text: o.textContent?.trim(),
      value: o.value,
      gender: o.getAttribute('player-gender'),
      group: /** @type {HTMLOptGroupElement | null} */ (o.parentElement?.tagName === 'OPTGROUP' ? o.parentElement : null)?.label ?? '',
    })));
  }
  shortsLog('PLAYERS', 'DOM → player tab:', !!document.querySelector('button[data-tab="player"]'),
    '| #player panel:', !!document.getElementById('player'),
    '| #kit-player:', !!select,
    '| options (incl. placeholder):', select?.options.length ?? 0,
    '| disabled:', select?.disabled ?? '—');
  if (players.hide_players_display) {
    shortsWarn('PLAYERS', 'REASON: customization.hide_players_display is set on the base product, so the panel is never rendered.');
  } else if (players.has_players === null) {
    shortsWarn('PLAYERS', 'REASON: has_players is nil → the player panel loop did not run (customization block hidden?).');
  } else if (!players.has_players) {
    shortsWarn('PLAYERS', 'REASON: no players_display matched season + activity + role, so the inline hasPlayers script removed the tab. See the table above.');
  } else if (!select) {
    shortsWarn('PLAYERS', 'has_players is true but #kit-player is not in the DOM.');
  }
  shortsGroupEnd();
}

// When the main query returns 0 candidates, re-runs minimal queries
// (handle only), removing/adding filters to pinpoint the filter that wipes out
// all results. `filters` are the same strings that were sent.
async function fictDiagnoseEmptyFilters(/** @type {string[]} */ filters, /** @type {any} */ variables) {
  if (!KIT_DEBUG) return;

  const label = (/** @type {string | undefined} */ f) => ((f || '').match(/key:\s*"([^"]+)"/) || [])[1] || f || '';

  // Returns the nodes (handle + kit_type/competition/season/base_product) so we
  // can inspect WHICH products match each subset, not just how many.
  const runNodes = async (/** @type {string[]} */ subset) => {
    const block = subset.length ? `filters: [${subset.join(',')}]` : '';
    const q = `query FictDiag($countryCode: CountryCode!, $languageCode: LanguageCode!) @inContext(country: $countryCode, language: $languageCode) {
      collection(handle: "all-products") {
        products(first: 250, ${block}) {
          edges { node {
            handle
            kit_type: metafield(namespace: "customization", key: "kit_type") { reference { ... on Metaobject { handle } } }
            competition: metafield(namespace: "customization", key: "competition") { references(first: 10) { edges { node { ... on Metaobject { handle } } } } }
            season: metafield(namespace: "customization", key: "season") { reference { ... on Metaobject { handle } } }
            base_product: metafield(namespace: "customization", key: "base_product") { value }
          } }
        }
      }
    }`;
    try {
      const r = await shopifyQuery(q, variables);
      return r?.data?.collection?.products?.edges?.map((/** @type {any} */ e) => e.node) ?? [];
    } catch (e) {
      return null;
    }
  };
  const runCount = async (/** @type {string[]} */ subset) => {
    const nodes = await runNodes(subset);
    return nodes === null ? -1 : nodes.length;
  };
  const detail = (/** @type {any} */ n) => ({
    handle: n.handle,
    kit_type: n.kit_type?.reference?.handle ?? '—',
    competition: (n.competition?.references?.edges ?? []).map((/** @type {any} */ e) => e.node?.handle).join('|') || '—',
    season: n.season?.reference?.handle ?? '—',
    base_product: n.base_product?.value?.split('/').pop() ?? '—',
  });
  // Lists a subset's products when there are few of them (to avoid flooding the console).
  const dumpNodes = async (/** @type {string} */ title, /** @type {string[]} */ subset) => {
    const nodes = await runNodes(subset);
    if (!nodes || nodes.length === 0 || nodes.length > 25) return;
    fictLog(`   detail "${title}" (${nodes.length}):`);
    fictTable(nodes.map(detail));
  };

  fictGroup('Filter diagnosis (find which one leaves 0 results)');

  const full = await runCount(filters);
  fictLog(`With ALL filters (${filters.length}): ${full} results`);

  fictLog('— Leave-one-out (removing one filter at a time) —');
  for (let i = 0; i < filters.length; i++) {
    const name = label(filters[i]);
    const subset = filters.filter((_, j) => j !== i);
    const count = await runCount(subset);
    fictLog(
      `WITHOUT "${name}": ${count} results` +
        (count > 0 ? '  ← ⚠️ this filter is (part of) the problem' : ''),
    );
    if (count > 0) await dumpNodes(`WITHOUT ${name}`, subset);
  }

  fictLog('— Cumulative (adding filters one by one) —');
  const running = /** @type {string[]} */ ([]);
  for (let i = 0; i < filters.length; i++) {
    running.push(filters[i] || '');
    const count = await runCount(running);
    fictLog(
      `Up to "${label(filters[i])}" (${running.length} filters): ${count} results` +
        (count === 0 ? '  ← ❌ drops to 0 HERE' : ''),
    );
  }

  fictGroupEnd();
}

// Two independent signals, deliberately kept apart.
//
// isCustomizationActive answers "is this a customizable kit PDP?": the block is on
// the page, so sizes must go through selectSize() and resolve a fictitious product.
//
// isCustomizationPlayerActive answers the narrower "is the player dropdown
// rendered?". It disappears when no players_display matches the product or when the
// base product sets customization.hide_players_display, and only the player-specific
// paths care. Tying the first flag to the second used to drop the whole
// customization flow — fictitious resolution and canvas refresh on size change —
// for every product without a player list.
const isCustomizationActive =
  document.getElementsByClassName("kit-customization").length > 0;
const isCustomizationPlayerActive =
  document.getElementById("kit-player") !== null;

// Function to get URL query parameters
function getUrlParameter(name) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

// Detect if competition=no-competition is in the URL
const competitionParam = getUrlParameter("competition");
const isNoCompetitionMode = competitionParam === "no-competition";

// Detect if serigraphy GID is in the URL
const serigraphyParam = getUrlParameter("serigraphy");
let customSerigraphyOptions = null;

// Detect customization parameters from URL
const nameParam = getUrlParameter("name");
const numberParam = getUrlParameter("number");

// Detect if there's URL customization and set flags EARLY (before any renders)
const hasUrlCustomization = !!(
  serigraphyParam ||
  competitionParam ||
  nameParam ||
  numberParam
);

// Expose to global window for KitCanvasRenderer
window.serigraphyParam = serigraphyParam;
window.nameParam = nameParam;
window.numberParam = numberParam;
window.competitionParam = competitionParam;

if (hasUrlCustomization) {
  // Pre-calculate how many canvas updates we expect
  let expectedUpdates = 0;
  if (serigraphyParam) expectedUpdates++;
  if (competitionParam && competitionParam !== "no-competition")
    expectedUpdates++;
  if (nameParam || numberParam) expectedUpdates++;

  window.urlCustomizationApplied = true;
  window.pendingCanvasUpdates = expectedUpdates;
} else {
  window.urlCustomizationApplied = false;
  window.pendingCanvasUpdates = 0;
}

// Read multi-reference kit pool embedded by kit-pdp-pool.liquid
function readPdpProductPool() {
  const poolScriptTag = document.getElementById('kit-pdp-product-pool');
  if (!poolScriptTag) {
    console.log('[Kit Pool] No #kit-pdp-product-pool tag found — single-product mode');
    return { prePrinted: [], blank: [] };
  }
  try {
    const pool = JSON.parse(poolScriptTag.textContent);
    console.log(`[Kit Pool] Pool loaded — pre-printed: ${pool.prePrinted.length}, blank: ${pool.blank.length}`);
    return pool;
  } catch (err) {
    console.error('[Kit Pool] Failed to parse pool JSON:', err);
    return { prePrinted: [], blank: [] };
  }
}

const pdpProductPool = readPdpProductPool();
const isMultiPoolMode = pdpProductPool.prePrinted.length > 0 || pdpProductPool.blank.length > 0;

// Cache for getVariantLetterLimits(). Declared here (not next to the function) because
// setupSizeSelectors() runs during module evaluation, before later `let` declarations.
/** @type {{ defaultLimit: number|null, fallbackLimit: number, byVariantId: Map<string, number|null> }|null} */
let variantLetterLimits = null;

if (isMultiPoolMode) {
  console.log('[Kit Pool] Multi-pool mode ACTIVE', {
    prePrinted: pdpProductPool.prePrinted.length,
    blank: pdpProductPool.blank.length,
  });
}

// maps metaobject fields array to a key-value object
function extractMetaobjectFields(metaobjectFields) {
  const result = {};

  if (!metaobjectFields || !Array.isArray(metaobjectFields.fields)) {
    return result;
  }

  for (const field of metaobjectFields.fields) {
    let value = field.value;

    if (field.type === "boolean") {
      value = value === "true";
    } else if (field.type === "number_decimal") {
      // A blank decimal field comes back as null and parseFloat(null) is NaN.
      // NaN is not nullish, so it used to sail through applySerigraphyOptions
      // and land on the canvas, where the 2D context silently IGNORES every
      // invalid value (lineWidth, the font size inside the font string) and
      // keeps whatever the previous render left in the context — the same
      // long-lived context object every render shares. That is how a blank
      // field on one competition inherited the previous competition's stroke
      // or font. Normalize to null so blank means "keep the base default",
      // exactly like the Liquid-rendered defaults already do.
      const parsed = parseFloat(value);
      value = Number.isFinite(parsed) ? parsed : null;
    }

    result[field.key] = value;
  }

  return result;
}

// Parses a serigraphy rendered by the kit-serigraphy-json snippet. Keys mirror
// the serigraphy metaobject field keys (snake_case). Returns null when there is
// nothing to read.
function parseSerigraphyJson(/** @type {string | undefined} */ raw) {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return parsed && Object.keys(parsed).length ? parsed : null;
  } catch (err) {
    console.warn("[kit] Failed to parse serigraphy:", err);
    return null;
  }
}

// Reads the product's default serigraphy (the first entry of its
// customization.serigraphy list), downloaded via Liquid as a JSON data attribute
// on the canvas element. Returns null when the product has no serigraphy metafield.
function readProductDefaultSerigraphy(/** @type {string} */ canvasId) {
  const canvas = document.getElementById(canvasId);
  return parseSerigraphyJson(canvas?.dataset?.serigraphyDefaults);
}

// Reads the serigraphy picked in the customization style selector, which only
// renders for kits without competition that list several serigraphies. Returns
// null when there is no selector, so the product default applies.
function readSelectedSerigraphy() {
  const radio = /** @type {HTMLInputElement | null} */ (
    document.querySelector(".kit-serigraphy-radio:checked")
  );
  return parseSerigraphyJson(radio?.dataset?.serigraphyOptions);
}

// Handle of the serigraphy the printing is made with, sent to the cart as the
// _serigraphy property. Same precedence as the canvas: the selected
// competition's serigraphy, then the style picked in the selector, then the
// product default.
function getActiveSerigraphyHandle() {
  if (!isNoCompetitionMode) {
    const competitionRadio = /** @type {HTMLInputElement | null} */ (
      document.querySelector(".kit-competition-radio:checked")
    );
    if (competitionRadio?.dataset.serigraphyHandle) {
      return competitionRadio.dataset.serigraphyHandle;
    }
  }

  const styleRadio = /** @type {HTMLInputElement | null} */ (
    document.querySelector(".kit-serigraphy-radio:checked")
  );
  if (styleRadio?.value) return styleRadio.value;

  return (
    document.getElementById("customization-id")?.dataset.defaultSerigraphy ||
    null
  );
}

// Returns the first argument that is a non-empty, non-whitespace value.
// Used to resolve a special color with a fallback to a default one.
function firstNonEmpty(/** @type {...any} */ ...values) {
  for (const value of values) {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed) return trimmed;
  }
  return undefined;
}

// Fetch serigraphy metaobject by GID from Storefront API
async function fetchSerigraphyMetaobject(gid) {
  const query = `
    query GetSerigraphyMetaobject($id: ID!) {
      metaobject(id: $id) {
        handle
        type
        fields {
          key
          value
          type
        }
      }
    }
  `;

  try {
    const res = await shopifyQuery(query, { id: gid });

    if (!res?.data?.metaobject) {
      console.warn(
        `[fetchSerigraphyMetaobject] Metaobject not found for GID: ${gid}`,
      );
      return null;
    }

    return extractMetaobjectFields(res.data.metaobject);
  } catch (err) {
    console.error(
      `[fetchSerigraphyMetaobject] Failed to fetch metaobject:`,
      err,
    );
    return null;
  }
}

function scrollToCustomizationImage(isFirstLoad) {
  if (window.innerWidth > 750) {
    function getCustomizationSlideIndex() {
      const image = document.getElementById("kit-customization-image");
      if (!image) return -1;

      const targetSlide = image.closest(".kit-media__slide");
      const allSlides = document.querySelectorAll(".kit-media__slide");

      return Array.from(allSlides).indexOf(targetSlide);
    }

    const index = getCustomizationSlideIndex();
    if (index !== -1) {
      window.media.goTo(index);
      window.media.updateActiveThumbnail(index);
    }
  } else if (isFirstLoad) {
    const slide = document.getElementById("kit-customization-slide");
    if (!slide) return;

    // Equivalent to scrollIntoView({ block: "center" }) but offset 50px down
    // so the page scrolls up 50px less than a perfect center.
    const rect = slide.getBoundingClientRect();
    const centerTarget =
      window.scrollY + rect.top - (window.innerHeight - rect.height) / 2;

    window.scrollTo({ top: centerTarget + 50, behavior: "smooth" });
  }
}

function formatPrice(priceInCents) {
  const htmlElement = document.documentElement;
  const lang = htmlElement.getAttribute("lang");
  const country = htmlElement.getAttribute("country");
  const currency = htmlElement.getAttribute("currency");
  const currencySymbol = htmlElement.getAttribute("currency-symbol");

  const locale = `${lang}-${country}`;

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const price = priceInCents / 100;
  const formattedPrice = formatter.format(price);

  if (formattedPrice === null || formattedPrice === undefined) {
    return `${currencySymbol}0 ${currency}`;
  }

  return `${currencySymbol}${formattedPrice} ${currency}`;
}

function updatePrices() {
  const priceElements = document.querySelectorAll("[data-raw-price]");

  priceElements.forEach((el) => {
    const rawPrice = parseFloat(el.getAttribute("data-raw-price"));
    if (!isNaN(rawPrice)) {
      el.textContent = formatPrice(rawPrice);
    }
  });
}

function updateBadgePrice() {
  const selectedBadge = document.querySelector(
    ".kit-competition-radio:checked",
  );
  const priceContainer = document.querySelector(
    ".kit-customization-badge-price",
  );

  if (!selectedBadge || !priceContainer) return;

  const priceWrapper = priceContainer.closest(".kit-competition-price");
  const priceInCents = parseInt(selectedBadge.getAttribute("data-badge-price"));

  if (!isNaN(priceInCents)) {
    priceContainer.textContent = formatPrice(priceInCents);
    if (priceWrapper) priceWrapper.style.display = "";
  } else {
    // No badge price (e.g. the "Sin parche" competition adds no extra charge):
    // hide the whole "+ price" line so neither the price nor the "+" shows.
    priceContainer.textContent = "";
    if (priceWrapper) priceWrapper.style.display = "none";
  }
}

// Global cache of all options (except the placeholder)
let __allPlayerOptionsCache = null;

function updatePlayerDisplayOptions(badgeGenderArg) {
  const select = document.getElementById("kit-player");
  if (!select) return;

  // 1) Initialize the cache only once
  if (!__allPlayerOptionsCache) {
    __allPlayerOptionsCache = [];
    for (let i = 1; i < select.options.length; i++) {
      const o = select.options[i];
      // Store all the required attributes
      __allPlayerOptionsCache.push({
        text: o.textContent,
        value: o.value,
        attrs: Array.from(o.attributes).map((a) => [a.name, a.value]),
      });
    }
  }

  // 2) Determine the active gender (if not passed in, read it from the checked radio)
  let badgeGender = (badgeGenderArg || "").toLowerCase();
  if (!badgeGender) {
    const checked = document.querySelector(".kit-competition-radio:checked");
    if (checked?.dataset.gender)
      badgeGender = checked.dataset.gender.toLowerCase();
  }
  // Empty gender (e.g. the "Sin parche" competition, which carries no gender)
  // means no gender filter: fall through and show every player instead of
  // bailing out and leaving a stale, previously-filtered list.

  // 3) Clear and repopulate the select (keeping the placeholder)
  const placeholder = select.options[0]?.cloneNode(true);
  select.innerHTML = "";
  if (placeholder) select.appendChild(placeholder);

  const selectedRef = select.dataset.selectedBackReference || "";

  __allPlayerOptionsCache.forEach((item) => {
    // Look up the player-gender attribute in the snapshot
    const genderPair = item.attrs.find(([k]) => k === "player-gender");
    const optGender = (genderPair?.[1] || "").toLowerCase();

    if (!badgeGender || optGender === badgeGender) {
      const opt = document.createElement("option");
      opt.textContent = item.text;
      opt.value = item.value;
      // restore ALL attributes
      item.attrs.forEach(([k, v]) => opt.setAttribute(k, v));

      if (selectedRef && opt.getAttribute("player-reference") === selectedRef) {
        opt.selected = true;
      }
      select.appendChild(opt);
    }
  });

  // Disable it if there are no players
  select.disabled = select.options.length <= 1;
  shortsLog('PLAYERS', 'updatePlayerDisplayOptions → gender filter:', badgeGender || '(none)',
    `| cached: ${__allPlayerOptionsCache.length} | shown: ${select.options.length - 1}`,
    select.disabled ? '| select DISABLED (no players left after the gender filter)' : '');

  // If the previously selected option disappeared, fall back to the placeholder
  if (select.selectedIndex <= 0) {
    select.selectedIndex = 0;
  }

  // If your logic needs to recompute something after repainting, dispatch an optional change:
  // select.dispatchEvent(new Event('change', { bubbles: true }));
}

document.querySelectorAll(".kit-competition-radio").forEach((radio) => {
  radio.addEventListener("change", updateBadgePrice);
  radio.addEventListener("click", scrollToCustomizationImage);

  if (isCustomizationPlayerActive) {
    radio.addEventListener("change", () => {
      const select = /** @type {HTMLSelectElement | null} */ (document.getElementById("kit-player"));
      const currentValue = select?.value;
      const previousGender = select?.querySelector(`option[value="${currentValue}"]`)?.getAttribute("player-gender")?.toLowerCase();
      const newGender = (radio.dataset.gender || "").toLowerCase();
      updatePlayerDisplayOptions(newGender);

      let playerWasReset = false;
      if (currentValue && select && previousGender === newGender) {
        select.value = currentValue;
        if (select.value !== currentValue) {
          select.selectedIndex = 0;
          playerWasReset = true;
        }
      } else if (currentValue && previousGender !== newGender) {
        select.selectedIndex = 0;
        playerWasReset = true;
      }

      if (playerWasReset && product) {
        product.customization.playerName = "";
        product.customization.playerNumber = "";
        product.customization.playerReference = null;
        product.customization.backID = null;
        product.updateCustomizationType();
      }
    });
  }

  // The competition font feeds the canvas for every customization mode, so it is
  // preloaded whenever the block is on the page, player dropdown or not.
  if (isCustomizationActive) {
    const serigraphyCompetitionFont = radio.dataset.serigraphy || null;
    if (serigraphyCompetitionFont) {
      preloadFont(serigraphyCompetitionFont);
    }
  }
});

  function pickFictitiousVariant(/** @type {any} */ fictitious, /** @type {string|null} */ sizeLabel) {
    const nodes = (fictitious?.variants?.edges ?? []).map((/** @type {any} */ e) => e.node);
    const node =
      nodes.find((/** @type {any} */ n) => n.selectedOptions?.some((/** @type {any} */ o) => o.value === sizeLabel)) ??
      nodes[0];
    if (!node) return null;
    return {
      productId: String(node.id).split('/').pop(), 
      unitPrice: parseFloat(node.price.amount),
      currency: node.price.currencyCode,
    };
  }

class ProductBase {
  constructor(productData) {
    this.id = productData.id;
    this.handle = productData.handle;
    this.title = productData.title;
    this.basePrice = productData.basePrice;
    this.sizes = productData.sizes;
    this.images = productData.images;
    this.kit = productData.kit;
    this.quality = productData.quality;
    this.role = productData.role;
    this.sport = productData.sport;
    this.productType = productData.productType;
    this.ageGroup = productData.ageGroup;
    this.targetGender = productData.targetGender;
    this.competition = null;
    this.variantID = null;
    /** @type {HTMLButtonElement | null} */
    this.submitButton = null;
    this.customization = {
      backID: "",
      kitID: "",
      qualityID: "",
      roleID: "",
      sportID: "",
      seasonID: "",
      playerName: "",
      playerNumber: null,
      playerReference: null,
      playerSeason: productData.season,
      badge: productData.badge,
      size: null,
      type: productData.kitType,
      initialType: productData.kitType,
    };
    /** @type {string | null} ID of the base product determined by the multi-pool logic */
    this.currentBaseProductId = null;
    // Id of the latest updateVariantID() request. Every name/number keystroke,
    // tab switch or competition change fires one, and the Storefront API does
    // not answer in order: only the latest request may apply its result.
    this.variantRequestId = 0;
    // Reference of the selected player whose player fictitious does not exist,
    // so the customization falls back to a custom fictitious carrying the
    // player's name and number. Set and cleared by updateVariantID() only.
    /** @type {string | null} */
    this.playerFallbackReference = null;
    // Resolved by populateAddToCartForm() on every render and before submitting.
    // Initialized here so the add-to-cart guards can read it unconditionally.
    this.state = {
      baseIsReturnable: false,
      customizationIsReturnable: false,
      badgeIsReturnable: false,
      /** @type {string | null} */
      badgeVariantId: null,
    };
  }
  updateCustomizationType() {
    const { type, playerReference } = this.customization;

    const suffixes = [
      "-player-fictitious",
      "-custom-fictitious",
      "-fictitious",
      "-base",
    ];
    let prefix = type;

    for (const suffix of suffixes) {
      if (type?.endsWith(suffix)) {
        prefix = type.slice(0, -suffix.length);
        break;
      }
    }

    // A base product reached directly (e.g. a Google Shopping link) has
    // kit_type "base": its prefix is "base" and window.kit_type["base-*"] is undefined,
    // which removes the kit_type filter and matches any fictitious product. When
    // customized it must behave like a real line item. We decide which one by the ACTUAL
    // presence of a competition selector in the DOM (not by isNoCompetitionMode, which
    // only exists when arriving via ?competition=...):
    //   • with a competition patch → "competition"      → competition-{player,custom}-fictitious
    //   • without competition (baby kits) → "no-competition" → no-competition-{player,custom}-fictitious
    if (prefix === "base") {
      const hasCompetition =
        document.querySelector(".kit-competition-radio") !== null;
      prefix = hasCompetition ? "competition" : "no-competition";
    }


    const playerTab = document.querySelector(
      '.kit-customization-tab[data-tab="player"]',
    );
    const customTab = document.querySelector(
      '.kit-customization-tab[data-tab="custom"]',
    );
    // Either field can be absent: the customization scope drops #kit-name or
    // #kit-number, so read both optionally and let isEmpty() treat the missing
    // one as empty.
    const inputKitPlayer = /** @type {HTMLInputElement | null} */ (
      document.getElementById("kit-name")
    );
    const inputKitNumber = /** @type {HTMLInputElement | null} */ (
      document.getElementById("kit-number")
    );

    let suffix = "fictitious";

    // Kits without competition have no plain "fictitious" product (there is no
    // no-competition-fictitious kit_type): with nothing to print they are the
    // base product itself, whichever tab is open. Left as "fictitious", an
    // emptied custom tab dropped the kit_type filter and matched any product,
    // so the page stayed on the last custom fictitious.
    const emptySuffix = prefix === "no-competition" ? "base" : "fictitious";

    if (
      customTab?.classList.contains("active") &&
      (!isEmpty(inputKitPlayer?.value) || !isEmpty(inputKitNumber?.value))
    ) {
      suffix = "custom-fictitious";
    } else {
      suffix = emptySuffix;
    }

    if (playerTab?.classList.contains("active")) {
      if (playerReference) {
        suffix =
          this.playerFallbackReference === playerReference
            ? "custom-fictitious"
            : "player-fictitious";
      } else {
        suffix = emptySuffix;
      }
    }

    // Pre-printed kits never have a plain "-fictitious" product by definition: the
    // competition is already printed on the base, so without a player or a custom
    // print the shopper is looking at the base product itself.
    if (suffix === "fictitious" && prefix === "pre-printed-competition") {
      suffix = "base";
    }

    this.customization.type = `${prefix}-${suffix}`;

    fictLog('updateCustomizationType →', {
      computedType: this.customization.type,
      prefix,
      suffix,
      initialType: this.customization.initialType,
      playerReference: this.customization.playerReference || '(none)',
      playerName: this.customization.playerName || '(empty)',
      playerNumber: this.customization.playerNumber || '(empty)',
    });
  }

  selectSize(sizeID) {
    this.variantID = sizeID;
    this.customization.size = this.getSizeLabelFromId(sizeID);

    if (this.customization.size) {
      this.highlightSizeError(false);
    }

    this.updateSizeUI();
    this.updateVariantID();
  }

  // Fetches the fictitious candidates for one customization type: builds the
  // Storefront filters from the current customization and runs the query.
  // Throws when the request fails. Matching is left to getMatchingProduct().
  async fetchFictitiousCandidates(/** @type {string} */ type) {
    const { backID, kitID, qualityID, roleID, sportID, seasonID } =
      this.customization;
    const gid = (id) => (id ? `gid://shopify/Metaobject/${id}` : null);
    const filters = [];

    const baseKeys = {
      kit: { id: kitID, ns: "customization" },
      quality: { id: qualityID, ns: "customization" },
      role: { id: roleID, ns: "customization" },
      season: { id: seasonID, ns: "customization" },
    };

    for (const [key, { id, ns }] of Object.entries(baseKeys)) {
      if (id) {
        filters.push(
          `{ productMetafield: { namespace: "${ns}", key: "${key}", value: "${gid(
            id,
          )}" } }`,
        );
      }
    }

    if (sportID) {
      filters.push(
        `{ productMetafield: { namespace: "shopify", key: "sport", value: "${gid(sportID)}" } }`,
      );
    }

    // The product type is what tells garments of the same kit apart (shirt vs
    // shorts): they share kit, quality, role, season and sport.
    if (this.productType) {
      filters.push(`{ productType: ${JSON.stringify(this.productType)} }`);
    }

    if (type?.includes("competition-player-fictitious") && backID) {
      filters.push(
        `{ productMetafield: { namespace: "customization", key: "back", value: "${gid(
          backID,
        )}" } }`,
      );
    }

    const kitTypeID = window.kit_type?.[type];
    if (kitTypeID) {
      filters.push(
        `{ productMetafield: { namespace: "customization", key: "kit_type", value: "${gid(
          kitTypeID,
        )}" } }`,
      );
    }

    const filterBlock = filters.length ? `filters: [${filters.join(",")}]` : "";
    const query = `
      query ProductsFiltered($countryCode: CountryCode!, $languageCode: LanguageCode!)
      @inContext(country: $countryCode, language: $languageCode) {
        collection(handle: "all-products") {
          products(first: 250, ${filterBlock}) {
            edges {
              node {
                id
                title
                handle
                tags
                productType
                images(first: 10) {
                  edges { node { url } }
                }
                  collections(first: 100) {
                    edges {
                      node {
                        id
                        handle
                        title
                      }
                    }
                  }
                metafields(identifiers: [
                  { namespace: "label", key: "custom"}
                  { namespace: "shopify", key: "age-group" },
                  { namespace: "shopify", key: "target-gender" },
                  { namespace: "shopify", key: "color-pattern" },
                  { namespace: "shopify", key: "size" },
                  { namespace: "customization", key: "competition" },
                  { namespace: "customization", key: "quality" },
                  { namespace: "customization", key: "season" },
                  { namespace: "customization", key: "kit" },
                  { namespace: "customization", key: "color" },
                  { namespace: "customization", key: "role" },
                  { namespace: "shopify", key: "sport" },
                  { namespace: "custom", key: "badges" },
                  { namespace: "customization", key: "back" },
                  { namespace: "customization", key: "custom" },
                  { namespace: "customization", key: "player_image" },
                  { namespace: "customization", key: "render_image" },
                  { namespace: "customization", key: "base_product" }
                ]) {
                  key
                  value
                  type
                  reference {
                    ... on Metaobject {
                      handle
                      type
                      fields { key value }
                    }
                    ... on MediaImage {
                      image { url }
                    }
                  }
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          handle
                          type
                          fields {
                            key
                            value
                            reference {
                              ... on Metaobject {
                                fields { key value type }
                              }
                              ... on MediaImage {
                                image { url }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      title
                      price { amount currencyCode }
                      selectedOptions { name value }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      countryCode: window.countryCode,
      languageCode: window.languageCode,
    };

    fictGroup(`updateVariantID — building Storefront API query (${type})`);
    fictLog('customization.type:', type);
    fictLog(
      'kit_type ID (window.kit_type[type]):',
      kitTypeID ?? '⚠️ UNDEFINED → kit_type filter SKIPPED (check window.kit_type and the handle)',
    );
    fictLog('metaobject IDs (customization):', {
      kitID,
      qualityID,
      roleID,
      seasonID,
      sportID,
      backID: backID || '(none)',
    });
    fictLog('currentBaseProductId (multi-pool):', this.currentBaseProductId ?? '(null)');
    fictLog(
      '#kit-product-base-id (hidden input):',
      /** @type {HTMLInputElement | null} */ (document.querySelector('#kit-product-base-id'))?.value ?? '(none)',
    );
    fictLog('selected competition handle (DOM):', getSelectedCompetitionHandle() ?? '(none)');
    fictLog(`filters sent to the API (${filters.length}):`, filters);
    fictGroupEnd();

    const res = await shopifyQuery(query, variables);
    const products =
      res?.data?.collection?.products?.edges?.map((edge) => edge.node) || [];
    fictLog(
      `Storefront API returned ${products.length} candidate(s) for ${type}:`,
      products.map((/** @type {any} */ p) => p.handle),
    );
    if (products.length === 0) {
      fictWarn(
        'The API returned no candidates → no fictitious product matches the filters. ' +
          'Suspect an overly restrictive filter (kit_type, back, season, kit, quality, role) ' +
          'or that the fictitious product is not in the "all-products" collection.',
      );
      // Debug only, and several extra queries: never hold the result for it.
      void fictDiagnoseEmptyFilters(filters, variables);
    }
    return products;
  }

  async updateVariantID() {
    // Every new search tries the player fictitious first: a competition, size
    // or base product change may have made it available, so the fallback
    // decided by a previous search is not carried over.
    if (this.playerFallbackReference) {
      this.playerFallbackReference = null;
      this.updateCustomizationType();
    }

    const { type, playerReference } = this.customization;

    // Players display backup: a selected player with no player fictitious is
    // printed as custom, with the player's name and number. The custom search
    // runs alongside the player one rather than after it, so the fallback costs
    // no extra round trip: the player fictitious still wins whenever it exists.
    const fallbackType =
      playerReference && type?.endsWith("-player-fictitious")
        ? type.replace(/-player-fictitious$/, "-custom-fictitious")
        : null;

    const requestId = ++this.variantRequestId;
    shortsLog('CUSTOM', `#${requestId} updateVariantID SENT`, {
      type,
      fallbackType: fallbackType ?? '(none)',
      name: this.customization.playerName,
      number: this.customization.playerNumber,
    });

    try {
      const [products, fallbackProducts] = await Promise.all([
        this.fetchFictitiousCandidates(type),
        // A failed fallback search must not sink the player one.
        fallbackType
          ? this.fetchFictitiousCandidates(fallbackType).catch((err) => {
              console.error("[updateVariantID] Custom fallback search failed:", err);
              return [];
            })
          : [],
      ]);

      // A newer request was sent while this one was in flight. Its result
      // describes a customization the shopper has already moved past, and
      // applying it would overwrite the newer match once it lands earlier.
      if (requestId !== this.variantRequestId) {
        shortsWarn('CUSTOM', `#${requestId} updateVariantID DISCARDED: request #${this.variantRequestId} is newer.`);
        return;
      }

      this.matchingProduct = this.getMatchingProduct(products, type);

      if (!this.matchingProduct && fallbackType) {
        this.matchingProduct = this.getMatchingProduct(fallbackProducts, fallbackType);
        if (this.matchingProduct) {
          // From here on the customization is the custom one: the cart's
          // _custom_type and every later updateCustomizationType() follow it.
          this.playerFallbackReference = playerReference;
          this.updateCustomizationType();
          shortsWarn('CUSTOM', `#${requestId} no player fictitious for "${playerReference}" → custom fallback ${this.matchingProduct.handle}`);
        }
      }

      shortsLog('CUSTOM',
        `#${requestId} updateVariantID RESOLVED → ${this.matchingProduct?.handle ?? 'NO MATCH'}`,
        {
          typeSent: type,
          typeNow: this.customization.type,
          numberNow: this.customization.playerNumber,
          candidates: products.length,
          fallbackCandidates: fallbackType ? fallbackProducts.length : '(none)',
        },
      );
      fictLog(
        this.matchingProduct
          ? `✅ FICTITIOUS RESOLVED: ${this.matchingProduct.handle}`
          : '❌ NO MATCH: getMatchingProduct() returned null (see table above)',
      );
    } catch (err) {
      // Same rule as above: a failed stale request must not clear a newer match.
      if (requestId !== this.variantRequestId) return;
      console.error("[updateVariantID] ❌ FAILED TO FETCH PRODUCTS:", err);
      this.matchingProduct = null;
    }

    this.populateAddToCartForm();
    this.updateUI();
  }

  // `type` is the customization type the candidates were fetched for. It is
  // not always the current one: the players display fallback matches custom
  // candidates while a player is still selected.
  getMatchingProduct(productsResult, type = this.customization.type) {
    try {
      const playerReference = this.customization.playerReference?.toLowerCase();
      // The `competition` metafield stores a metaobject *handle* (e.g.
      // "laligawinners-home-2627"), but badge.title is the human label with
      // spaces ("LaLigaWinners Home 2627"). Comparing those never matches, so we
      // resolve the actual selected competition handle from the DOM (same source
      // used by the multi-pool logic) and fall back to slugifying the title.
      const badge =
        getSelectedCompetitionHandle()?.toLowerCase() ??
        this.customization.badge?.title?.toLowerCase().replace(/\s+/g, "-");
      const seasonFormatted = this.customization?.playerSeason
        ?.replace("/", "-")
        .toLowerCase();
      // In multi-pool mode, use the dynamically determined base product ID.
      // Falls back to the static hidden input for single-product pages.
      const expectedBase = this.currentBaseProductId ??
        /** @type {HTMLInputElement | null} */ (document.querySelector("#kit-product-base-id"))?.value;
      const isNoCompetition =
        isNoCompetitionMode ||
        type?.includes("no-competition");

      const checksBack = type.includes("player");
      fictGroup(`getMatchingProduct — evaluating ${productsResult.length} candidate(s)`);
      fictLog('EXPECTED VALUES (from base product + selection):', {
        base_product: expectedBase ?? '(undefined)',
        competition_handle: badge ?? '(none)',
        isNoCompetition,
        kit: this.kit?.toLowerCase(),
        quality: this.quality?.toLowerCase(),
        role: this.role?.toLowerCase(),
        sport: this.sport?.toLowerCase(),
        productType: this.productType ?? '(none)',
        season_info: seasonFormatted ?? '(none)',
        playerReference: playerReference ?? '(none)',
        checksBack,
        type,
      });
      /** @type {any[]} */
      const fictReport = [];

      const matchingProducts = productsResult.filter((product) => {
        const metafieldsArray = Array.isArray(product.metafields)
          ? product.metafields.filter(Boolean)
          : [];
        const metafields = Object.fromEntries(
          metafieldsArray.map((mf) => [mf.key, mf]),
        );

        const baseProductId = metafields?.base_product?.value?.split("/").pop();
        const seasonHandle =
          metafields?.season?.reference?.handle?.toLowerCase();
        const competitionHandles =
          metafields?.competition?.references?.edges?.map((e) =>
            e.node.handle?.toLowerCase(),
          ) || [];
        const kit = metafields?.kit?.reference?.handle?.toLowerCase();
        const quality = metafields?.quality?.reference?.handle?.toLowerCase();
        // [ROLE-LIST-TEMP] TEMPORARY WORKAROUND — DO NOT MERGE INTO MAIN UNTIL
        // FIXED. customization.role has become a list metafield, which the API
        // returns under `references` instead of `reference`. A product has
        // exactly one role, so only the first value is read until the
        // definition goes back to a single reference.
        const role = (
          metafields?.role?.reference ??
          metafields?.role?.references?.edges?.[0]?.node
        )?.handle?.toLowerCase();
        const sportHandles =
          metafields?.sport?.references?.edges?.map((edge) =>
            edge.node?.handle?.toLowerCase(),
          ) || [];
        const backHandle = metafields?.back?.reference?.handle?.toLowerCase();

        // A "-base" type resolves to the base product itself, which carries no
        // customization.base_product pointing at itself: match it by its own ID.
        const isBaseType = type?.endsWith("-base");
        const matchesBase =
          baseProductId === expectedBase ||
          (isBaseType && product.id?.split("/").pop() === expectedBase);
        const matchesSeason = seasonHandle === seasonFormatted;
        const matchesCompetition =
          isNoCompetition || competitionHandles.includes(badge); // ← skip if no-competition
        const matchesKit = kit === this.kit?.toLowerCase();
        const matchesQuality = quality === this.quality?.toLowerCase();
        const matchesRole = role === this.role?.toLowerCase();
        const matchesSport = sportHandles.includes(this.sport?.toLowerCase());
        const matchesProductType =
          !this.productType || product.productType === this.productType;
        let matchesBack = true;
        let isCustomValid = true;

        if (type.includes("player")) {
          matchesBack = backHandle === playerReference;
        }

        if (
          type.toLowerCase().includes("custom") &&
          isEmpty(this.customization.playerName) &&
          isEmpty(this.customization.playerNumber)
        ) {
          isCustomValid = false;
        }

        const allMatch =
          matchesBase &&
          matchesCompetition &&
          matchesKit &&
          matchesQuality &&
          matchesRole &&
          matchesSport &&
          matchesProductType &&
          matchesBack &&
          isCustomValid;

        if (KIT_DEBUG) {
          const mark = (/** @type {boolean} */ ok) => (ok ? '✓' : '✗');
          fictReport.push({
            candidate: product.handle,
            base: `${baseProductId ?? '—'} (own id ${product.id?.split('/').pop() ?? '—'}) ${mark(matchesBase)}`,
            competition: `${competitionHandles.join('|') || '—'} ${matchesCompetition ? '✓' : '✗→' + (badge ?? '?')}`,
            kit: `${kit ?? '—'} ${mark(matchesKit)}`,
            quality: `${quality ?? '—'} ${mark(matchesQuality)}`,
            role: `${role ?? '—'} ${mark(matchesRole)}`,
            sport: `${sportHandles.join('|') || '—'} ${mark(matchesSport)}`,
            productType: `${product.productType || '—'} ${mark(matchesProductType)}`,
            back: checksBack ? `${backHandle ?? '—'} ${matchesBack ? '✓' : '✗→' + (playerReference ?? '?')}` : 'n/a',
            custom: isCustomValid ? '✓' : '✗ (missing name/number)',
            season_info: `${seasonHandle ?? '—'} vs ${seasonFormatted ?? '—'} ${mark(matchesSeason)}`,
            RESULT: allMatch ? '✅ MATCH' : '❌ reject',
          });
        }

        return allMatch;
      });

      fictTable(fictReport);
      fictLog(
        matchingProducts.length > 0
          ? `Winner: ${matchingProducts[0].handle}`
          : 'No candidate satisfied ALL conditions. Check which column(s) show ✗ in the table.',
      );
      fictGroupEnd();

      return matchingProducts.length > 0 ? matchingProducts[0] : null;
    } catch (err) {
      console.error("getMatchingProduct failed", err);
      return null;
    }
  }

  // Single source of truth for whether the current customization type ships the
  // competition patch as its own line item. Pre-printed kits already carry the
  // patch printed on the garment, and no-competition kits have none at all.
  requiresCompetitionBadge() {
    // Normal (non-kit) products share this add-to-cart flow. They have no
    // kit_type, and updateCustomizationType() still leaves a synthetic
    // "undefined-fictitious" type behind, so gate on initialType (same signal
    // the payload uses as `isKit`) rather than on the type string alone.
    if (!this.customization.initialType) return false;

    // Without a selector in the DOM the shopper has no way to pick a patch, so
    // requiring one would dead-end the page instead of protecting the order.
    if (document.querySelector(".kit-competition-radio") === null) return false;

    // A competition can intentionally ship without a patch (the "Sin parche"
    // option, whose metaobject references no product_badge). Liquid marks it with
    // data-no-badge="true". This is distinct from an empty badge-id caused by an
    // error (no radio checked, or the patch product has no variant available in
    // the market), which must still block — hence the explicit flag rather than
    // inferring from a missing badge-id.
    const checkedRadio = /** @type {HTMLInputElement | null} */ (
      document.querySelector(".kit-competition-radio:checked")
    );
    if (checkedRadio?.dataset.noBadge === "true") return false;

    const type = this.customization.type;
    if (!type) return false;

    return !(
      type.includes("no-competition") ||
      type.includes("pre-printed-competition")
    );
  }

  highlightCompetitionError(show = true) {
    const competitionSection = document.querySelector(
      ".kit-customization-competition",
    );
    if (!competitionSection) return;

    if (show) {
      competitionSection.classList.add("has-error");
      competitionSection.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else {
      competitionSection.classList.remove("has-error");
    }
  }

  highlightSizeError(show = true) {
    const sizeOptions = document.querySelector("#fcb-product-options-size");
    if (!sizeOptions) return;

    sizeOptions.querySelectorAll('[data-fieldset-index]').forEach((g) => g.classList.remove('option-error'));

    if (show) {
      if (!sizeOptions.dataset.labelNoVariantOriginal) {
        sizeOptions.dataset.labelNoVariantOriginal = sizeOptions.dataset.labelNoVariant;
      }
      const missingGroup = Array.from(sizeOptions.querySelectorAll('[data-fieldset-index]'))
        .find((group) => !group.querySelector('input[type="radio"]:checked'));
      const missingName = missingGroup?.dataset.optionName;
      if (missingName) {
        const prefix = sizeOptions.dataset.labelNoOption || 'Please select a';
        const name = missingName.charAt(0).toUpperCase() + missingName.slice(1).toLowerCase();
        sizeOptions.dataset.labelNoVariant = ` ${prefix} ${name}`;
        missingGroup.classList.add('option-error');
      } else {
        sizeOptions.dataset.labelNoVariant = sizeOptions.dataset.labelNoVariantOriginal;
      }
      sizeOptions.classList.add("has-error");
      sizeOptions.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      sizeOptions.classList.remove("has-error");
    }
  }

  // Name and number as they can actually be printed. The customization scope
  // can leave either out (Liquid marks it on .kit-customization), and a chosen
  // player still carries both, so the canvas and the cart read them through here
  // rather than straight from this.customization.
  getPrintablePersonalization() {
    const block = /** @type {HTMLElement | null} */ (
      document.querySelector(".kit-customization")
    );
    return {
      name:
        block?.dataset.scopeName === "false" ? "" : this.customization.playerName,
      number:
        block?.dataset.scopeNumber === "false"
          ? ""
          : this.customization.playerNumber,
    };
  }

  populateAddToCartForm() {
    this.updateCustomizationType();
    const baseInput = document.getElementById("kitVariantIdInput");
    const customizationInput = document.getElementById("customization-id");
    const badgeInput = document.querySelector(".kit-competition-radio:checked");

    this.state = {
      baseIsReturnable: baseInput?.dataset.isreturnable === "true",
      customizationIsReturnable:
        customizationInput?.dataset.isreturnable === "true",
      badgeIsReturnable: badgeInput?.dataset.isreturnable === "true",
      badgeVariantId: badgeInput?.getAttribute("badge-id") || null,
    };

    // Update hidden form inputs for clarity/debugging
    const printable = this.getPrintablePersonalization();
    baseInput.value = this.variantID || "";
    document.getElementById("kitPlayerNameInput").value = printable.name || "";
    document.getElementById("kitPlayerNumberInput").value =
      printable.number || "";
    document.getElementById("kitSeasonInput").value =
      this.customization.playerSeason || "";
    document.getElementById("kitBadgeInput").value =
      this.customization.badge?.title || "";
    document.getElementById("kitBackInput").value =
      this.customization.playerReference || "";
    document.getElementById("kitSizeInput").value =
      this.customization.size || "";
  }

  async captureCustomizationImage() {
    if (!this.canvas || typeof this.canvas.exportImageBlob !== "function") {
      console.warn(
        "Canvas not initialized or exportImageBlob() method not available",
      );
      return null;
    }

    try {
      const blob = await this.canvas.exportImageBlob();
      return blob;
    } catch (error) {
      console.error("Error capturing canvas image:", error);
      return null;
    }
  }

  async addToCartSubmission() {
  if (!this.customization.size) {
    this.highlightSizeError(true);
    this.submitButton?.removeAttribute("aria-disabled");
    this.submitButton?.classList.remove("loading");
    this.submitButton?.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
    return;
  }

  if (!this.variantID) return;

  try {
    this.populateAddToCartForm();

    // The patch is a separate line item and /cart/add.js happily accepts a
    // payload without it, so a missing badge variant used to produce a kit with
    // no competition patch and no error at all. Block the submission instead.
    // Known causes: no radio ends up :checked (the `selected` class is derived
    // from `product`'s competition metafield while the labels come from
    // `base_product`'s), or badge-id renders empty because the patch product has
    // no available variant in the current market.
    const skipBadge = !this.requiresCompetitionBadge();

    if (!skipBadge && !this.state.badgeVariantId) {
      console.error(
        "[kit] Add to cart blocked: a competition patch is required but none was resolved",
        {
          customizationType: this.customization.type,
          selectedCompetitionHandle: getSelectedCompetitionHandle(),
          hasCheckedRadio:
            document.querySelector(".kit-competition-radio:checked") !== null,
        },
      );
      this.highlightCompetitionError(true);
      return;
    }

    this.highlightCompetitionError(false);

    const isKit = !!this.customization.initialType;

    const printable = this.getPrintablePersonalization();
    const hasCustomization =
      printable.name?.trim() || printable.number?.trim();

    const isCard = this.submitButton?.dataset.isCardOk === "true";

    const customizationKey = hasCustomization
      ? `-${printable.name?.trim().toUpperCase() || 'no-name'}-${printable.number?.trim() || 'no-number'}`
      : '';
    const bundleId = (isKit && !isCard)
      ? `det-${this.variantID}-${(!skipBadge && this.state.badgeVariantId) ? this.state.badgeVariantId : 'no-badge'}${customizationKey}`
      : crypto.randomUUID();

    const parentProperties = {
      _is_returnable: this.state.baseIsReturnable,
    };
    if (isKit) {
      parentProperties._product_id = bundleId;
      parentProperties._bundle_id = bundleId;
    }
    if (isCard) {
      parentProperties._unique_id = 'CARD-' + Date.now() + '-' + Math.random().toString(36).substring(7);
    }
    if (isKit && this.customization.type) {
      parentProperties._custom_type = this.customization.type;
    }
    if (this.matchingProduct?.handle) {
      parentProperties._fictitious_url = `/products/${this.matchingProduct.handle}`;
    }
    const fv = pickFictitiousVariant(this.matchingProduct, this.customization.size);
    if (fv) parentProperties._fictitious_variant_id = fv.productId;

    const items = [
      { id: this.variantID, quantity: 1, properties: parentProperties },
    ];

    if (hasCustomization) {
      // The printing line item: the base product's customization.customization_service
      // variant, or the generic Customization product when it has none. Liquid
      // resolves which one into #customization-id.
      const customizationVariantId = document.getElementById("customization-id")?.value;
      if (customizationVariantId) {
        /** @type {Record<string, any>} */
        const customizationProperties = {
          _related_product_id: bundleId,
          _bundle_id: bundleId,
          name: printable.name?.toUpperCase(),
          number: printable.number,
          _is_returnable: this.state.customizationIsReturnable,
        };
        const serigraphyHandle = getActiveSerigraphyHandle();
        if (serigraphyHandle) {
          customizationProperties._serigraphy = serigraphyHandle;
        }
        items.push({
          id: customizationVariantId,
          quantity: 1,
          parent_id: this.variantID,
          properties: customizationProperties,
        });
      }
    }

    if (!skipBadge && this.state.badgeVariantId) {
      items.push({
        id: this.state.badgeVariantId,
        quantity: 1,
        parent_id: this.variantID,
        properties: {
          _related_product_id: bundleId,
          _bundle_id: bundleId,
          _is_returnable: this.state.badgeIsReturnable,
        },
      });
    }

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) throw new Error("Failed to add items to cart");

    const data = await res.json();

    document.dispatchEvent(new CustomEvent("addToCartSuccess", { detail: data }));
    document.dispatchEvent(new CartAddEvent());
    
    if (window.kitDataLayer && window.kitDataLayer.dispatchEvent) {
      window.kitDataLayer.dispatchEvent({
        category: "ecommerce",
        action: "cart_add",
        placement: "pdp",
        context: { product: window.kitDataLayer.enrichProductBase() },
      });
    }
  } catch (error) {
    console.error("Error during add to cart:", error);
  } finally {
    this.submitButton?.removeAttribute("aria-disabled");
    this.submitButton?.classList.remove("loading");
    this.submitButton?.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
  }
}

  getSizeLabelFromId(sizeId) {
    const match = this.sizes.find((size) => size.id === sizeId);
    return match?.label || null;
  }

  updatePlayer(
    playerName,
    playerNumber,
    playerReference,
    playerSeason,
    backID = null,
  ) {
    this.customization.playerName = playerName;
    this.customization.playerNumber = playerNumber;
    this.customization.playerReference = playerReference;
    this.customization.playerSeason = playerSeason;
    this.customization.backID = backID;
    this.updateCustomizationType();
    this.updateVariantID().then(() => this.updateCanvas());
  }

  updatePlayerName(name) {
    this.customization.playerName = name;
    this.updateCustomizationType();
    this.updateCanvas();
  }

  updatePlayerNumber(number) {
    this.customization.playerNumber = number;
    this.updateCustomizationType();
    this.updateCanvas();
  }

  updateCompetition(badge) {
    this.customization.badge = badge;
    if (badge?.variantId) {
      this.highlightCompetitionError(false);
    }
    this.updateCustomizationType();
    this.updateVariantID().then(() => this.updateCanvas());
  }

  updateCanvas(config) {
    const configuration = this.getCanvasConfiguration(config);

    const printable = this.getPrintablePersonalization();
    shortsLog('SCOPE', 'canvas paints →', { name: printable.name, number: printable.number },
      '| raw customization →', { name: this.customization.playerName, number: this.customization.playerNumber });
    product.canvas = new KitCanvasRenderer(
      printable.name,
      printable.number,
      configuration.elements,
      configuration.options,
      configuration.badges,
    );
  }

  updateSizeUI() {
    const wrapper = document.getElementById('fcb-product-options-size');
    if (!wrapper || !this.variantID) return;

    const jsonScript = wrapper.querySelector('script[type="application/json"][id^="kit-variants-json-"]');
    if (!jsonScript) return;

    let variants;
    try {
      variants = JSON.parse(jsonScript.textContent);
    } catch {
      return;
    }

    const variant = variants.find((v) => String(v.id) === String(this.variantID));
    if (!variant) return;

    wrapper.querySelectorAll('[data-fieldset-index]').forEach((fieldset) => {
      const idx = parseInt(fieldset.dataset.fieldsetIndex, 10);
      const optionValue = variant[`option${idx + 1}`];
      if (!optionValue) return;

      fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const input = /** @type {HTMLInputElement} */ (radio);
        input.checked = (input.dataset.label ?? input.value) === optionValue;
      });
    });
  }

  updateProductUI() {
    if (!this.matchingProduct) return;

    const titleEl = document.querySelector(".kit-base-info .kit-title");
    const fictitiousPriceEl = document.getElementById("total-price-container");
    const firstVariant = this.matchingProduct.variants?.edges?.[0]?.node;
    const fictitiousPrice = parseFloat(firstVariant?.price?.amount || 0) * 100;

    if (titleEl && titleEl.textContent !== this.matchingProduct.title) {
      titleEl.textContent = this.matchingProduct.title;
    }

    if (fictitiousPrice && fictitiousPriceEl) {
      fictitiousPriceEl.textContent = formatPrice(fictitiousPrice);

      // Keep the struck-through subtotal in step: total + saving on base and printing
      // (rendered by Liquid in data-kit-discount). Without a saving it is left as rendered.
      const compareEl = document.getElementById("total-compare-container");
      const kitDiscount = parseFloat(compareEl?.dataset.kitDiscount || "0");
      if (compareEl && kitDiscount > 0) {
        const compare = fictitiousPrice + kitDiscount;
        compareEl.dataset.rawCompare = String(compare);
        compareEl.textContent = formatPrice(compare);
      }
    }

    this.handle = this.matchingProduct.handle;
    this.title = this.matchingProduct.title;

    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const pathParts = currentPath.split("/");
    const localeRegex = /^[a-z]{2}(-[a-z]{2})?$/i;
    let localePrefix = "";

    if (pathParts[1] && localeRegex.test(pathParts[1])) {
      localePrefix = `/${pathParts[1]}`;
    }
    const newUrl = `${localePrefix}/products/${this.handle}${currentSearch}`;
    window.history.replaceState(window.history.state, "", newUrl);
  }

  updateUI() {
    this.updateSizeUI();
    this.updateProductUI();
  }

  getCanvasConfiguration(params) {
    const defaultBadges = removeDuplicates(window.kit_badges || []);
    const configuration = {
      elements: {
        imgId: "kit-customization-image",
        canvasId: "kit-customization-canvas",
      },
      options: {
        arc: true,
        numberDots: true,
        textFont: "FCB-Play",
        nameFontSizeRatio: 0.0744,
        nameStrokeWidthRatio: 0,
        curvedNameArcCenterYPositionRatio: 0.7193,
        curvedNameArcRadiusRatio: 0.3836,
        nameKerning: 0.0038,
        nameStraightKerning: null,
        straightNameYPositionRatio: 0.381,
        numberYPositionRatio: 0.759,
        numberFontSizeRatio: 0.435,
        numberStrokeWidthRatio: 0,
        numberJoinStyle: "miter",
        numberKerning: null,
        numberDieCut: false,
        // Signed horizontal offset of the name/number center, as a ratio of the
        // rendered image width. 0 keeps the canvas center. The key has to exist
        // here or applySerigraphyOptions drops it: it only overrides options that
        // are already declared.
        centerXPositionRatio: 0,
        // Same reason: declared so applySerigraphyOptions lets them through.
        numberRotationY: 0,
        numberPerspectiveDistanceRatio: 0.5,
        numberRotationZ: 0,
        removeBackground: hasUrlCustomization ? true : false,
      },
      badges: defaultBadges,
    };

    if (params?.elements) {
      configuration.elements = params.elements;
    }

    function snakeToCamel(str) {
      return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    }

    // Applies a serigraphy option set (keyed by metaobject field keys) onto
    // configuration.options, converting keys to camelCase and only overriding
    // options that already exist. Null/undefined values are skipped so a missing
    // field keeps the base default instead of blanking it out. Non-finite
    // numbers are skipped for the same reason and as a second line of defence:
    // the canvas ignores invalid geometry instead of erroring, which turns a bad
    // value into the previous render's value rather than into a visible fault.
    const applySerigraphyOptions = (/** @type {Record<string, any> | null | undefined} */ source) => {
      if (!source) return;
      for (const [key, value] of Object.entries(source)) {
        if (value === null || value === undefined) continue;
        if (typeof value === "number" && !Number.isFinite(value)) continue;
        const camelKey = snakeToCamel(key);
        if (camelKey in configuration.options) {
          configuration.options[camelKey] = value;
        }
      }
    };

    // Kits whose competition is not shopper-selected as a separate patch: either
    // there is no competition at all, or it comes printed on the garment. Used
    // only to pick the hardcoded fallback below for products that still have no
    // serigraphy metafield.
    const isStaticSerigraphy =
      this.customization?.type?.includes("no-competition") ||
      this.customization?.type?.includes("pre-printed-competition");

    // Whether the selected competition dictates the typeface. Pre-printed kits
    // used to be excluded here together with no-competition ones, which pinned
    // their font to the base product's serigraphy: switching competition swapped
    // the fictitious product but never the lettering. They do have a competition
    // metaobject (it is printed on the garment rather than sewn on as a patch)
    // and it carries its own serigraphy, so it has to win exactly like it does
    // for patch-based kits. Only no-competition kits have nothing to read from.
    const competitionDictatesSerigraphy =
      !this.customization?.type?.includes("no-competition");

    // Default serigraphy dictated by the product itself, downloaded via Liquid
    // as a JSON data attribute on the canvas. Replaces the previously hardcoded
    // defaults: used on first load and as the base for every kit type. When the
    // customization style selector is on the page, the picked style takes its
    // place: it starts on the product default and only exists on kits without
    // competition, so nothing below overrides it.
    const productDefaultSerigraphy =
      readSelectedSerigraphy() ??
      readProductDefaultSerigraphy(configuration.elements.canvasId);

    if (productDefaultSerigraphy) {
      applySerigraphyOptions(productDefaultSerigraphy);
    } else if (isStaticSerigraphy) {
      // Fallback for products that don't have a serigraphy metafield yet.
      applySerigraphyOptions({
        arc: true,
        numberDots: false,
        textFont: "FCB-Modernista-Home",
        nameFontSizeRatio: 0.0977,
        nameStrokeWidthRatio: 0.002,
        curvedNameArcCenterYPositionRatio: 0.71,
        curvedNameArcRadiusRatio: 0.3836,
        nameKerning: -0.0038,
        straightNameYPositionRatio: 0.3452,
        numberYPositionRatio: 0.759,
        numberFontSizeRatio: 0.5713,
        numberStrokeWidthRatio: 0.004,
        numberJoinStyle: "round",
      });
    }

    // The serigraphy that ends up describing the number. Tracked because the
    // merge below is not enough for options where an empty field has to mean
    // "off" rather than "inherit".
    let activeSerigraphy = productDefaultSerigraphy;

    // Priority 1: Custom serigraphy from query parameter (overrides everything).
    if (customSerigraphyOptions) {
      applySerigraphyOptions(customSerigraphyOptions);
      activeSerigraphy = customSerigraphyOptions;
    }
    // Priority 2: Competition serigraphy overrides the product default for
    // competition-based kits once the matching product is resolved.
    else if (this.matchingProduct && competitionDictatesSerigraphy) {
      const metafields = Array.isArray(this.matchingProduct.metafields)
        ? this.matchingProduct.metafields.filter(Boolean)
        : [];
      const metafieldCompetition = metafields.find(
        (mt) => mt.key === "competition",
      );
      const serigraphyReference =
        metafieldCompetition?.references?.edges?.[0]?.node?.fields?.find(
          (f) => f?.key === "serigraphy",
        )?.reference;
      const serigraphyOptions = extractMetaobjectFields(serigraphyReference);
      // A competition with no serigraphy metaobject at all yields an empty
      // object. Taking it as the active serigraphy would blank out the
      // product-level number_die_cut below, so the product default stays in
      // charge instead. A competition that *has* a serigraphy still wins whole,
      // blank fields included — that is what the numberDieCut note below relies
      // on.
      const hasCompetitionSerigraphy = Object.keys(serigraphyOptions).length > 0;
      if (hasCompetitionSerigraphy) {
        applySerigraphyOptions(serigraphyOptions);
        activeSerigraphy = serigraphyOptions;
      }
    }

    // number_die_cut is a boolean the admin can leave empty, and empty means no
    // die cut. applySerigraphyOptions skips null and absent values so a
    // serigraphy with blank ratios keeps the base defaults, but that also let the
    // flag from the product's default serigraphy survive a switch to a
    // competition whose serigraphy leaves it blank: the number kept being die cut
    // after changing competition. Read it from the serigraphy that won instead of
    // from the merged result, so blank and absent both land on false.
    configuration.options.numberDieCut = activeSerigraphy?.number_die_cut === true;

    // center_x_position_ratio behaves the same way: blank means "centered", not
    // "keep whatever the previous serigraphy said". Left to the merge, switching
    // to a competition whose serigraphy leaves the field empty would inherit the
    // offset from the product default and the number would stay off-center after
    // the change. Read it from the serigraphy that won, so blank, absent and
    // unparseable all land on 0.
    const centerXRatio = parseFloat(activeSerigraphy?.center_x_position_ratio);
    configuration.options.centerXPositionRatio = Number.isFinite(centerXRatio)
      ? centerXRatio
      : 0;

    // The number rotations too: blank means "no rotation". Otherwise a competition
    // whose serigraphy leaves it empty would keep the product default's
    // perspective. The distance only matters with a rotation, so blank falls
    // back to the renderer default.
    const rotationY = parseFloat(activeSerigraphy?.number_rotation_y);
    configuration.options.numberRotationY = Number.isFinite(rotationY) ? rotationY : 0;
    const rotationZ = parseFloat(activeSerigraphy?.number_rotation_z);
    configuration.options.numberRotationZ = Number.isFinite(rotationZ) ? rotationZ : 0;
    const perspectiveDistance = parseFloat(activeSerigraphy?.number_perspective_distance_ratio);
    configuration.options.numberPerspectiveDistanceRatio = Number.isFinite(perspectiveDistance) && perspectiveDistance > 0
      ? perspectiveDistance
      : 0.5;

    // Stroke widths too: blank means "no stroke". Left to the merge, a
    // competition serigraphy with an empty stroke kept the product default's
    // stroke, so the stroke looked inherited from one serigraphy to the next.
    // Only when a serigraphy won: without one, the hardcoded fallback above
    // carries its own strokes and must keep them.
    if (activeSerigraphy) {
      const nameStroke = parseFloat(activeSerigraphy.name_stroke_width_ratio);
      const numberStroke = parseFloat(activeSerigraphy.number_stroke_width_ratio);
      configuration.options.nameStrokeWidthRatio = Number.isFinite(nameStroke) ? nameStroke : 0;
      configuration.options.numberStrokeWidthRatio = Number.isFinite(numberStroke) ? numberStroke : 0;
    }

    if (this.matchingProduct) {
      const metafields = Array.isArray(this.matchingProduct.metafields)
        ? this.matchingProduct.metafields.filter(Boolean)
        : [];

      const metafieldBadges = metafields.find((mt) => mt.key === "badges");
      const badgeEdges = metafieldBadges?.references?.edges || [];

      if (badgeEdges.length) {
        for (const { node } of badgeEdges) {
          if (
            configuration.badges.some((badge) => badge.label == node.handle)
          ) {
            continue;
          }
          const badge = node.fields?.reduce((acc, item) => {
            const key = snakeToCamel(item.key);
            if (item.value?.includes("gid://shopify/MediaImage")) {
              acc[key] = item.reference?.image?.url;
            } else {
              acc[key] = item.value;
            }
            return acc;
          }, {});
          configuration.badges.push(badge);
        }
      }
    }

    // ── LaLiga competitions: dedicated name/number stroke color ──
    // When the selected competition metaobject has its `laliga` boolean set to
    // true, the name and number strokes use the customization_color metaobject's
    // `stroke_name_laliga` / `stroke_number_laliga` fields. When those are empty,
    // fall back to the regular `stroke` / `stroke_number` colors.
    if (this.matchingProduct) {
      const metafields = Array.isArray(this.matchingProduct.metafields)
        ? this.matchingProduct.metafields.filter(Boolean)
        : [];

      const competitionNode = metafields.find(
        (/** @type {any} */ mt) => mt.key === "competition",
      )?.references?.edges?.[0]?.node;
      const competitionFields = /** @type {any} */ (
        extractMetaobjectFields(competitionNode)
      );
      const isLaliga =
        competitionFields.laliga === true || competitionFields.laliga === "true";

      if (isLaliga) {
        const colorFields = /** @type {any} */ (
          extractMetaobjectFields(
            metafields.find((/** @type {any} */ mt) => mt.key === "color")?.reference,
          )
        );
        const opts = /** @type {any} */ (configuration.options);
        opts.nameStrokeColor = firstNonEmpty(
          colorFields.stroke_name_laliga,
          colorFields.stroke,
        );
        opts.numberStrokeColor = firstNonEmpty(
          colorFields.stroke_number_laliga,
          colorFields.stroke_number,
        );
      }
    }

    return configuration;
  }

  syncImagesWithClone(
    productImages,
    container,
    element,
    targetElement,
    specialId = null,
  ) {
    const slidesContainer = document.querySelector(container);
    if (!slidesContainer) return;

    const allSlides = Array.from(slidesContainer.querySelectorAll(element));
    const specialSlide = specialId
      ? slidesContainer.querySelector(`#${specialId}`)
      : null;

    const normalSlides = allSlides.filter(
      (slide) => !specialSlide || slide !== specialSlide,
    );

    if (normalSlides.length > productImages.length) {
      for (let i = productImages.length; i < normalSlides.length; i++) {
        normalSlides[i].remove();
      }
    }

    if (normalSlides.length < productImages.length && normalSlides.length > 0) {
      const cloneBase = normalSlides[0];
      for (let i = normalSlides.length; i < productImages.length; i++) {
        const newSlide = cloneBase.cloneNode(true);
        const img = newSlide.querySelector(targetElement);
        if (img) img.src = productImages[i];
        if (newSlide.getAttribute("data-index") !== null) {
          let plus = specialId ? 1 : 0;
          newSlide.setAttribute("data-index", `${i + plus}`);
        }
        slidesContainer.appendChild(newSlide);
        window.media.kitMediaLoad();
      }
    }

    const updatedNormalSlides = Array.from(
      slidesContainer.querySelectorAll(element),
    ).filter((slide) => !specialSlide || slide !== specialSlide);
    updatedNormalSlides.forEach((slide, i) => {
      const img = slide.querySelector(targetElement);
      if (img && productImages[i]) {
        img.src = productImages[i];
      }
    });
  }

  syncCanvasRenderImage(imgElement, canvasImg) {
    if (canvasImg) {
      imgElement.src = canvasImg;
      setTimeout(() => {
        product.updateCanvas();
      }, 100);
    }
  }
}

class ProductFictitious extends ProductBase {
  constructor(productData, fictitiousData) {
    super(productData);
    this.preSelectedVariantID = fictitiousData.preSelectedVariantID;
    this.preConfiguredCompetition = fictitiousData.preConfiguredCompetition;
    this.preConfiguredBack = fictitiousData.preConfiguredBack;
  }

  lockCustomizationOptions() {}
  applyPreConfiguredCompetition() {
    this.competition = this.preConfiguredCompetition;
  }
  applyPreConfiguredBack() {
    this.customization.playerName = this.preConfiguredBack.name;
    this.customization.playerNumber = this.preConfiguredBack.number;
  }
}

let hasTriggeredUpdateForName = false;
let hasTriggeredUpdateForNumber = false;
/** @type {any} */
let product = null;

// ======= Multi-Pool Functions (multi_reference_kit) =======


/**
 * Returns the handle of the currently selected competition from the DOM.
 * @returns {string|null}
 */
function getSelectedCompetitionHandle() {
  const checkedRadio = /** @type {HTMLInputElement | null} */ (document.querySelector('.kit-competition-radio:checked'));
  const parentLabel = /** @type {HTMLElement | null} */ (checkedRadio?.closest('.kit-competition-label'));
  return parentLabel?.dataset.competitionHandle ?? null;
}

/**
 * Sets product.currentBaseProductId to the primary product for the given competition.
 * Used as the default base product before a specific size is selected.
 * @param {any} filteredPool
 * @param {string|null} competitionHandle
 */
function setDefaultBaseProduct(filteredPool, competitionHandle) {
  const productInstance = /** @type {any} */ (product);

  const prePrintedMatch = competitionHandle
    ? filteredPool.prePrinted.find((/** @type {any} */ poolProduct) => poolProduct.competition_handles.includes(competitionHandle))
    : filteredPool.prePrinted[0] ?? null;

  const defaultProduct = prePrintedMatch ?? filteredPool.blank[0] ?? null;

  if (!defaultProduct) {
    console.warn('[Kit Pool] setDefaultBaseProduct — no product found in filtered pool');
    return;
  }

  console.log(`[Kit Pool] setDefaultBaseProduct → ${defaultProduct.handle} (competition: "${competitionHandle ?? 'none'}")`);
  productInstance.currentBaseProductId = defaultProduct.id;

  const baseIdInput = /** @type {HTMLInputElement | null} */ (document.querySelector('#kit-product-base-id'));
  if (baseIdInput) baseIdInput.value = defaultProduct.id;
}

/**
 * Builds a merged variant list from ALL pool products that have the given competition.
 * Pre-printed products take priority over blank for available sizes.
 * Each variant carries _baseProductId so the correct product is used when that size is added to cart.
 * @param {any} filteredPool
 * @param {string|null} competitionHandle
 * @returns {any[]}
 */
function buildMergedVariantsForCompetition(filteredPool, competitionHandle) {
  const prePrintedForCompetition = competitionHandle
    ? filteredPool.prePrinted.filter((/** @type {any} */ poolProduct) => poolProduct.competition_handles.includes(competitionHandle))
    : filteredPool.prePrinted;
  const blankForCompetition = competitionHandle
    ? filteredPool.blank.filter((/** @type {any} */ poolProduct) => poolProduct.competition_handles.includes(competitionHandle))
    : filteredPool.blank;

  // Collect all size titles across relevant products (pre-printed first to preserve natural size order)
  const allSizeTitles = /** @type {string[]} */ ([]);
  const seenSizeTitles = new Set();
  for (const poolProduct of [...prePrintedForCompetition, ...blankForCompetition]) {
    for (const variant of /** @type {any[]} */ (poolProduct.variants)) {
      if (!seenSizeTitles.has(variant.title)) {
        seenSizeTitles.add(variant.title);
        allSizeTitles.push(variant.title);
      }
    }
  }

  const merged = /** @type {any[]} */ ([]);

  for (const sizeTitle of allSizeTitles) {
    // Try pre-printed products first — find best available variant
    let bestVariant = /** @type {any} */ (null);
    let bestPoolProductId = /** @type {any} */ (null);
    let source = 'primary';

    for (const poolProduct of prePrintedForCompetition) {
      const availableVariant = (/** @type {any[]} */ (poolProduct.variants)).find(
        (variant) => variant.title === sizeTitle && variant.available
      );
      if (availableVariant) {
        bestVariant = availableVariant;
        bestPoolProductId = poolProduct.id;
        source = 'primary';
        break;
      }
    }

    // If not available in any pre-printed, try blank products
    if (!bestVariant) {
      for (const poolProduct of blankForCompetition) {
        const availableVariant = (/** @type {any[]} */ (poolProduct.variants)).find(
          (variant) => variant.title === sizeTitle && variant.available
        );
        if (availableVariant) {
          bestVariant = availableVariant;
          bestPoolProductId = poolProduct.id;
          source = 'backup';
          break;
        }
      }
    }

    if (bestVariant) {
      merged.push({ id: bestVariant.id, title: sizeTitle, available: true, _source: source, _baseProductId: bestPoolProductId });
    } else {
      // Size exists somewhere but has no stock — show as unavailable
      let unavailableVariant = /** @type {any} */ (null);
      let unavailableProductId = /** @type {any} */ (null);
      for (const poolProduct of [...prePrintedForCompetition, ...blankForCompetition]) {
        const found = (/** @type {any[]} */ (poolProduct.variants)).find((variant) => variant.title === sizeTitle);
        if (found) {
          unavailableVariant = found;
          unavailableProductId = poolProduct.id;
          break;
        }
      }
      if (unavailableVariant) {
        merged.push({ id: unavailableVariant.id, title: sizeTitle, available: false, _source: 'primary', _baseProductId: unavailableProductId });
      }
    }
  }

  return merged;
}

/**
 * @param {any[]} mergedVariants
 * @param {string|null} [sizeToPreserve]
 */
function renderSizeSelectorWithFallback(mergedVariants, sizeToPreserve = null) {
  const wrapper = document.getElementById('fcb-product-options-size');
  if (!wrapper || !mergedVariants.length) {
    console.warn('[Kit Pool] renderSizeSelectorWithFallback — missing wrapper or empty variants, skipping');
    return;
  }

  const summary = mergedVariants.map((/** @type {any} */ variant) => `${variant.title}:${variant.available ? 'avail' : 'unav'}(${variant._source})`);
  console.log(`[Kit Pool] renderSizeSelectorWithFallback — preserving: "${sizeToPreserve}", variants:`, summary);

  // Read original Liquid-rendered JSON BEFORE overwriting — needed to recover option1/option2/option3
  const jsonScript = wrapper.querySelector('script[type="application/json"][id^="kit-variants-json-"]');
  let originalVariants = /** @type {any[]} */ ([]);
  if (jsonScript) {
    try { originalVariants = JSON.parse(jsonScript.textContent); } catch {}
  }
  console.log('[Kit Pool] Merged variants:', summary);

  // Enrich merged variants with option fields (option1/option2/option3) so resolveVariantFromOptions() can match them.
  // Pool JSON only has id/title/available; the Liquid-rendered original JSON has the full option structure.
  const enrichedVariants = mergedVariants.map((/** @type {any} */ mergedVariant) => {
    const originalVariant = originalVariants.find((/** @type {any} */ original) => original.title === mergedVariant.title);
    if (originalVariant) {
      const optionFields = /** @type {any} */ ({});
      for (const key of Object.keys(originalVariant)) {
        if (key.startsWith('option')) optionFields[key] = originalVariant[key];
      }
      return { ...mergedVariant, ...optionFields };
    }
    // Fallback for blank-only sizes not present in original Liquid JSON (single-option products)
    return { ...mergedVariant, option1: mergedVariant.title };
  });
  console.log('[Kit Pool] Enriched variant option1 samples:', enrichedVariants.slice(0, 3).map((/** @type {any} */ enrichedVariant) => `${enrichedVariant.title}→${enrichedVariant.option1}`));

  // Update variants JSON — resolveVariantFromOptions() and updateSizeUI() read this
  if (jsonScript) {
    jsonScript.textContent = JSON.stringify(enrichedVariants);
  }

  // Update product.sizes so getSizeLabelFromId() works with merged IDs
  const productInstance = /** @type {any} */ (product);
  if (productInstance) {
    productInstance.sizes = enrichedVariants.map((/** @type {any} */ variant) => ({ id: String(variant.id), label: variant.title }));
  }

  // Update disabled state and data attribute on each size radio
  wrapper.querySelectorAll('[data-fieldset-index] input[type="radio"]').forEach((radio) => {
    const radioInput = /** @type {HTMLInputElement} */ (radio);
    const sizeLabel = radioInput.dataset.label ?? radioInput.value;
    const matchedVariant = enrichedVariants.find((/** @type {any} */ variant) => variant.title === sizeLabel);
    if (!matchedVariant) return;

    radioInput.disabled = !matchedVariant.available;
    radioInput.dataset.baseProductId = matchedVariant._baseProductId;

    const sizeOptionLabel = radioInput.closest('.kit-size-option, .kit-color-option');
    if (sizeOptionLabel) sizeOptionLabel.classList.toggle('disabled', !matchedVariant.available);
  });

  // Preserve previously selected size if still available
  if (sizeToPreserve) {
    let preserved = false;
    wrapper.querySelectorAll('[data-fieldset-index] input[type="radio"]').forEach((radio) => {
      const radioInput = /** @type {HTMLInputElement} */ (radio);
      const sizeLabel = radioInput.dataset.label ?? radioInput.value;
      if (!radioInput.disabled && sizeLabel === sizeToPreserve) {
        radioInput.checked = true;
        preserved = true;
        if (radioInput.dataset.baseProductId && productInstance) {
          productInstance.currentBaseProductId = radioInput.dataset.baseProductId;
          console.log(`[Kit Pool] Size "${sizeToPreserve}" preserved → base product: ${productInstance.currentBaseProductId}`);
        }
      }
    });
    if (!preserved) {
      console.log(`[Kit Pool] Size "${sizeToPreserve}" no longer available — clearing size selection`);
      wrapper.querySelectorAll('[data-fieldset-index] input[type="radio"]').forEach((radio) => {
        /** @type {HTMLInputElement} */ (radio).checked = false;
      });
      if (productInstance) {
        productInstance.variantID = null;
        productInstance.customization.size = null;
      }
    }
  }
}

/**
 * @param {any} filteredPool
 * @param {string|null} [selectedSizeTitle]
 */
function refreshCompetitionSelector(filteredPool, selectedSizeTitle = null) {
  console.log(`[Kit Pool] refreshCompetitionSelector — sizeFilter: "${selectedSizeTitle}"`);

  let availableHandles;

  if (selectedSizeTitle) {
    const productsWithSize = [
      ...filteredPool.prePrinted,
      ...filteredPool.blank,
    ].filter((/** @type {any} */ poolProduct) => poolProduct.variants.some((/** @type {any} */ poolVariant) => poolVariant.title === selectedSizeTitle && poolVariant.available));

    availableHandles = new Set(productsWithSize.flatMap((/** @type {any} */ poolProduct) => poolProduct.competition_handles));
    console.log(`[Kit Pool] Competitions available for size "${selectedSizeTitle}":`, [...availableHandles]);
  } else {
    availableHandles = new Set(
      [...filteredPool.prePrinted, ...filteredPool.blank].flatMap((/** @type {any} */ poolProduct) => poolProduct.competition_handles)
    );
    console.log('[Kit Pool] All available competitions (no size filter):', [...availableHandles]);
  }

  let checkedRadio = /** @type {HTMLInputElement | null} */ (null);
  let firstEnabledRadio = /** @type {HTMLInputElement | null} */ (null);

  document.querySelectorAll('.kit-competition-label').forEach((label) => {
    const handle = /** @type {HTMLElement} */ (label).dataset.competitionHandle;
    const radio = /** @type {HTMLInputElement | null} */ (label.querySelector('.kit-competition-radio'));
    if (!radio || !handle) return;

    const isAvailable = availableHandles.has(handle);
    radio.disabled = !isAvailable;
    label.classList.toggle('disabled', !isAvailable);

    if (radio.checked) checkedRadio = radio;
    if (isAvailable && !firstEnabledRadio) firstEnabledRadio = radio;
  });

  // If currently selected competition is disabled, switch to first enabled
  if (checkedRadio?.disabled && firstEnabledRadio) {
    console.log(`[Kit Pool] Checked competition disabled — auto-switching to: ${firstEnabledRadio.value}`);
    firstEnabledRadio.checked = true;
    firstEnabledRadio.closest('.kit-competition-label')?.classList.add('selected');
    checkedRadio.closest('.kit-competition-label')?.classList.remove('selected');
    firstEnabledRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/** @param {string|null} [sizeToPreserve] */
function refreshProductContext(sizeToPreserve = null) {
  if (!isMultiPoolMode) return;

  console.log(`[Kit Pool] refreshProductContext — sizeToPreserve: "${sizeToPreserve}"`);

  // Update competition availability based on selected size (if any)
  refreshCompetitionSelector(pdpProductPool, sizeToPreserve);

  const competitionHandle = getSelectedCompetitionHandle();
  setDefaultBaseProduct(pdpProductPool, competitionHandle);

  const mergedVariants = buildMergedVariantsForCompetition(pdpProductPool, competitionHandle);
  renderSizeSelectorWithFallback(mergedVariants, sizeToPreserve);

  console.log('[Kit Pool] refreshProductContext complete');
}

const productType = document.querySelector(".kit-title")?.dataset.productType;
const defaultBadge = document.querySelectorAll(".kit-competition-radio")[0];
const kitPlayerSelect = document.getElementById("kit-player");

// ======= Global Config from <select> =======
function getDatasetValue(selector, attribute) {
  const el = document.querySelector(selector);
  const value = el?.dataset?.[attribute] || null;
  return value;
}

function getTextContent(selector) {
  return document.querySelector(selector)?.textContent.trim() || "";
}

function getSizeOptions() {
  const wrapper = document.getElementById('fcb-product-options-size');
  if (!wrapper) return [];

  const jsonScript = wrapper.querySelector('script[type="application/json"][id^="kit-variants-json-"]');
  if (!jsonScript) return [];

  try {
    const variants = JSON.parse(jsonScript.textContent);
    return variants.map((v) => ({
      id: String(v.id),
      label: v.option1 || v.title,
    }));
  } catch {
    return [];
  }
}

function getProductImages() {
  return Array.from(document.querySelectorAll(".kit-media__slide img")).map(
    (img) => img.src,
  );
}

// Competition
function getDefaultBadgeData() {
  // If competition=no-competition in URL, return null
  if (isNoCompetitionMode) {
    return null;
  }

  // Prefer the radio that is actually checked (or whose label is .selected) so the
  // initial badge state matches the server-rendered selection (e.g. when landing on a
  // UCL fictitious product). Falling back to the first radio would silently force the
  // first competition, causing updateVariantID() to match the wrong fictitious product
  // and rewrite the URL during initial load.
  const badge =
    document.querySelector(".kit-competition-radio:checked") ||
    document.querySelector(".kit-competition-label.selected .kit-competition-radio") ||
    document.querySelector(".kit-competition-radio");
  return {
    title: /** @type {HTMLInputElement} */ (badge)?.value || "",
    variantId: badge?.getAttribute("badge-id") || null,
  };
}

const productData = {
  id: getDatasetValue(".kit-title", "productId"),
  handle: getDatasetValue(".kit-title", "productHandle"),
  title: getTextContent(".kit-title"),
  basePrice:
    document.querySelector(".kit-price")?.getAttribute("data-raw-price") || "0",
  sizes: getSizeOptions(),
  images: getProductImages(),
  badge: getDefaultBadgeData(), // Competition (null if no-competition mode)
  season: getDatasetValue(".kit-title", "season")?.toLowerCase(),
  kit: getDatasetValue(".kit-title", "kit")?.toLowerCase(),
  quality: getDatasetValue(".kit-title", "quality")?.toLowerCase(),
  role: getDatasetValue(".kit-title", "role")?.toLowerCase(),
  sport: getDatasetValue(".kit-title", "sport")?.toLowerCase(),
  productType: getDatasetValue(".kit-title", "productType"),
  ageGroup: getDatasetValue(".kit-title", "age-group")?.toLowerCase(),
  targetGender: getDatasetValue(".kit-title", "target-gender")?.toLowerCase(),
  kitType: isNoCompetitionMode
    ? "no-competition-base"
    : getDatasetValue(".kit-title", "productKitType")?.toLowerCase(),
};

function resolveVariantFromOptions() {
  const wrapper = document.getElementById('fcb-product-options-size');
  if (!wrapper) return null;

  const jsonScript = wrapper.querySelector('script[type="application/json"][id^="kit-variants-json-"]');
  if (!jsonScript) return null;

  let variants;
  try {
    variants = JSON.parse(jsonScript.textContent);
  } catch {
    return null;
  }

  const fieldsets = wrapper.querySelectorAll('[data-fieldset-index]');
  const selectedValues = {};

  fieldsets.forEach((fieldset) => {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (checked) {
      const idx = parseInt(fieldset.dataset.fieldsetIndex, 10);
      selectedValues[idx] = checked.dataset.label ?? checked.value;
    }
  });

  const optionCount = fieldsets.length;
  if (optionCount === 0) return null;
  if (Object.keys(selectedValues).length < optionCount) return null;

  const match = variants.find((variant) => {
    return Object.entries(selectedValues).every(([idx, value]) => {
      return variant[`option${parseInt(idx, 10) + 1}`] === value;
    });
  });

  return match ? String(match.id) : null;
}

product = new ProductBase(productData);
window.productBaseObject = product;
dumpShortsDebug();

// DOM is already parsed at module evaluation (ES module is deferred by default).
// Registering here ensures listeners are ready before window.load, preventing
// a timing bug where the user selects a size before window.load fires.
setupSizeSelectors();

// Single-variant products have no size selector, so variantID must be set here
// rather than in initializeProductPage() — otherwise ATC before window.load
// silently does nothing (variantID is null, addToCartSubmission returns early).
const _singleVariantWrapper = document.getElementById('fcb-product-options-size');
if (!_singleVariantWrapper && product) {
  const _singleJsonScript = document.getElementById('kit-base-info-single-variant');
  if (_singleJsonScript) {
    try {
      const _singleVariant = JSON.parse(_singleJsonScript.textContent);
      if (_singleVariant?.id) {
        const _variantId = String(_singleVariant.id);
        if (product.customization.initialType) {
          product.selectSize(_variantId);
        } else {
          product.variantID = _variantId;
          product.customization.size = _singleVariant.title || _variantId;
        }
      }
    } catch (err) {
      console.error('[kit] Failed to parse single-variant JSON:', err);
    }
  }
}

setupSerigraphySelectors();

// Badge selectors and the default competition selection must be ready before
// window.load so that populateAddToCartForm() finds a checked radio and
// includes the competition license in the cart. Same pattern as setupSizeSelectors().
if (!isNoCompetitionMode) {
  setupBadgeSelectors();

  extractSelectedPlayerData();

  const _defaultCompetitionRadio = /** @type {HTMLInputElement | null} */ (
    document.querySelector(".kit-competition-label.selected .kit-competition-radio")
  );
  if (_defaultCompetitionRadio && !_defaultCompetitionRadio.checked) {
    _defaultCompetitionRadio.checked = true;
    _defaultCompetitionRadio.dispatchEvent(
      new Event("change", { bubbles: true }),
    );
  }

  updatePlayerDisplayOptions(_defaultCompetitionRadio?.dataset.gender);
} else {
  const _competitionSection = /** @type {HTMLElement | null} */ (
    document.querySelector(".kit-customization-competition")
  );
  if (_competitionSection) {
    _competitionSection.style.display = "none";
  }
}

function extractSelectedPlayerData() {
  // Reads the player dropdown, so it needs that dropdown rather than the block.
  // #kit-player only exists inside .kit-customization, so this also implies
  // isCustomizationActive.
  if (!isCustomizationPlayerActive) {
    return;
  }

  const select = document.getElementById("kit-player");
  const title = document.getElementById("kit-title");
  const option = select?.options[select.selectedIndex];

  const name = option.getAttribute("player-name") || "";
  const number = option.getAttribute("player-number") || "";
  const reference = option.getAttribute("player-reference") || "";
  const season = option.getAttribute("player-season") || "";
  const backID = option.getAttribute("player-back-id") || "";
  const kitID = title.getAttribute("player-kit-id");
  const qualityID = title.getAttribute("player-quality-id");
  const roleID = title.getAttribute("player-role-id");
  const sportID = title.getAttribute("player-sport-id");
  const seasonID = title.getAttribute("player-season-id");

  product.customization.backID = backID;
  product.customization.kitID = kitID;
  product.customization.qualityID = qualityID;
  product.customization.roleID = roleID;
  product.customization.sportID = sportID;
  product.customization.seasonID = seasonID;

  return {
    name,
    number,
    reference,
    season,
    backID,
    kitID,
    qualityID,
    roleID,
    sportID,
    seasonID,
  };
}

const selectedPlayer = extractSelectedPlayerData();
if (selectedPlayer && product) {
  product.updatePlayer(
    selectedPlayer.name,
    selectedPlayer.number,
    selectedPlayer.reference,
    selectedPlayer.season,
    selectedPlayer.backID,
  );
}

function preloadFont(fontName) {
  const fontUrl = window.fontUrl;
  const match = fontUrl.match(/(\/cdn\/shop\/t\/\d+\/assets\/)/);
  const extractedPath = match ? match[1] : null;
  const font = new FontFace(fontName, `url(${extractedPath}${fontName}.woff2)`);

  return font
    .load()
    .then(function (loadedFont) {
      document.fonts.add(loadedFont);
    })
    .catch(function (error) {
      const fontBase = new FontFace(fontName, `url(/assets/${fontName}.woff2)`);
      return fontBase
        .load()
        .then(function (loadedFont) {
          document.fonts.add(loadedFont);
        })
        .catch(function (error) {
          console.error(`Error loading font ${fontName}:`, error);
        });
    });
}

function resetPlayerTab() {
  extractSelectedPlayerData();
  product.customization.playerName = "";
  product.customization.playerReference = null;
  product.customization.backID = null;
  product.updateCustomizationType();
  product.updateVariantID();
  product.updatePlayerNumber("");
  document
    .querySelectorAll("#custom input")
    ?.forEach((input) => (input.value = ""));
}

function resetCustomTab() {
  product.customization.playerReference = null;
  product.customization.playerName = "";
  product.customization.backID = null;
  product.updateCustomizationType();
  product.updateVariantID();
  product.updatePlayerNumber("");
  const playerSelect = document.querySelector("#player select");
  if (playerSelect) playerSelect.value = "";
}

function handleTabClick(tab) {
  const dataTab = tab.getAttribute("data-tab");

  if (!tab.classList.contains("active")) {
    // 🟢 First set all tabs and contents
    document
      .querySelectorAll(".kit-customization-tab")
      .forEach((t) => t.classList.remove("active"));
    document
      .querySelectorAll(".kit-customization-content > div")
      .forEach((c) => c.classList.remove("active"));

    tab.classList.add("active");
    document.getElementById(dataTab)?.classList.add("active");

    // 🔄 THEN apply logic AFTER the tab is active
    if (dataTab === "player") {
      hasTriggeredUpdateForName = false;
      hasTriggeredUpdateForNumber = false;
      resetCustomTab();
    }

    if (dataTab === "custom") {
      resetPlayerTab();
      updatePlayerDisplayOptions();
    }
  }

  document
    .querySelectorAll(".kit-customization-tab")
    .forEach((t) => t.classList.remove("active"));
  document
    .querySelectorAll(".kit-customization-content > div")
    .forEach((c) => c.classList.remove("active"));

  tab.classList.add("active");
  document.getElementById(dataTab)?.classList.add("active");

  scrollToCustomizationImage();
}

document.querySelectorAll(".kit-customization-tab").forEach((tab) => {
  tab.addEventListener("click", () => handleTabClick(tab));
});

/**
 * Reads the per-variant letter limits rendered by kit-customization.liquid (cached).
 */
function getVariantLetterLimits() {
  if (variantLetterLimits) return variantLetterLimits;

  const byVariantId = new Map();
  let defaultLimit = null;
  let fallbackLimit = 12;

  const jsonScript = document.getElementById('kit-variant-letter-limits');
  if (jsonScript) {
    try {
      const data = JSON.parse(jsonScript.textContent);
      defaultLimit = parseLetterLimit(data.default);
      fallbackLimit = parseLetterLimit(data.fallback) ?? fallbackLimit;
      const pairs = [...(data.base || []), ...(data.pool || []).flat()];
      for (const [variantId, limit] of pairs) {
        // Base product entries come first, so they win over duplicated pool entries
        if (!byVariantId.has(String(variantId))) byVariantId.set(String(variantId), parseLetterLimit(limit));
      }
    } catch (err) {
      console.error('[kit] Failed to parse variant letter limits:', err);
    }
  }

  variantLetterLimits = { defaultLimit, fallbackLimit, byVariantId };
  return variantLetterLimits;
}

/**
 * Updates #kit-name maxlength from the selected variant's letter_limit metafield.
 * With no variant selected, it falls back to the first available variant's limit.
 * The current value is only touched if it exceeds the new limit (extra letters removed).
 */
function syncNameLetterLimit() {
  const nameInput = /** @type {HTMLInputElement|null} */ (document.getElementById('kit-name'));
  if (!nameInput) return;

  const { defaultLimit, fallbackLimit, byVariantId } = getVariantLetterLimits();
  const variantId = resolveVariantFromOptions();
  const variantLimit = variantId ? byVariantId.get(String(variantId)) ?? null : null;

  applyMaxLength(nameInput, variantLimit ?? defaultLimit ?? fallbackLimit);
}

function setupSizeSelectors() {
  const wrapper = document.getElementById('fcb-product-options-size');
  if (!wrapper) return;

  // If the user selected a size before this module finished loading, the radio
  // is already checked in the DOM but no change event fired. Read that state now.
  if (product) {
    const preSelectedVariantId = resolveVariantFromOptions();
    if (preSelectedVariantId) {
      if (isCustomizationActive || isMultiPoolMode) {
        product.selectSize(preSelectedVariantId);
      } else {
        product.variantID = preSelectedVariantId;
        product.customization.size =
          product.getSizeLabelFromId(preSelectedVariantId) ||
          wrapper.querySelector('input[name="kit-size"]:checked')?.getAttribute('data-label') ||
          null;
        if (product.customization.size) product.highlightSizeError(false);
      }
    }
  }

  syncNameLetterLimit();

  let normalAbortController = null;

  wrapper.querySelectorAll('input[type="radio"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      if (radio.disabled) return;

      const jsonScript = wrapper.querySelector('script[type="application/json"][id^="kit-variants-json-"]');
      if (jsonScript) {
        try {
          const variants = JSON.parse(jsonScript.textContent);
          const changedIdx = parseInt(radio.closest('[data-fieldset-index]')?.dataset.fieldsetIndex ?? '-1', 10);
          if (changedIdx >= 0) {
            const compatibleVariants = variants.filter(
              (v) => v[`option${changedIdx + 1}`] === (radio.dataset.label ?? radio.value)
            );
            wrapper.querySelectorAll('[data-fieldset-index]').forEach((fieldset) => {
              const idx = parseInt(fieldset.dataset.fieldsetIndex, 10);
              if (idx === changedIdx) return;
              fieldset.querySelectorAll('input[type="radio"]').forEach((r) => {
                const availableWithCurrent = compatibleVariants.some(
                  (v) => v[`option${idx + 1}`] === (r.dataset.label ?? r.value) && v.available
                );
                const label = r.closest('.kit-color-option');
                if (label) {
                  label.classList.toggle('kit-color-option--disabled', !availableWithCurrent);
                  r.disabled = !availableWithCurrent;
                }
                if (!availableWithCurrent && r.checked) r.checked = false;
              });
            });
          }
        } catch {}
      }

      const variantId = resolveVariantFromOptions();

      syncNameLetterLimit();

      if (!product) return;

      if (!variantId) {
        product.variantID = null;
        product.customization.size = null;
        return;
      }

      // Multi-pool: update currentBaseProductId from the selected radio and refresh competition availability
      if (isMultiPoolMode) {
        const checkedSizeRadio = /** @type {HTMLInputElement | null} */ (
          wrapper.querySelector('[data-fieldset-index] input[type="radio"]:checked')
        );
        if (checkedSizeRadio?.dataset.baseProductId) {
          product.currentBaseProductId = checkedSizeRadio.dataset.baseProductId;
          console.log(`[Kit Pool] Size selected — currentBaseProductId: ${product.currentBaseProductId}`);
        }
        const sizeTitle = checkedSizeRadio?.dataset.label ?? checkedSizeRadio?.value ?? null;
        refreshCompetitionSelector(pdpProductPool, sizeTitle);
        // Note: refreshCompetitionSelector may dispatch a competition change event,
        // which will re-run renderSizeSelectorWithFallback with size preservation.
        // selectSize below will then update the fictitious product with correct baseProductId.
      }

      if (isCustomizationActive || isMultiPoolMode) {
        // selectSize triggers updateVariantID(), which fetches the fictitious product.
        // In multi-pool mode this is always needed (competition fictitious at minimum),
        // and it avoids refreshNormalProductSection overwriting the enriched pool variants JSON.
        product.selectSize(variantId);
      } else {
        product.variantID = variantId;
        product.customization.size = product.getSizeLabelFromId(variantId);
        if (product.customization.size) product.highlightSizeError(false);
        refreshNormalProductSection(wrapper, variantId, normalAbortController, (ctrl) => {
          normalAbortController = ctrl;
        });
      }
    });
  });
}

function refreshNormalProductSection(wrapper, variantId, abortController, setAbortController) {
  const productUrl = wrapper.dataset.productUrl;
  const sectionId = wrapper.dataset.sectionId;
  if (!productUrl || !sectionId) return;

  const selectedOptionValueIds = Array.from(
    wrapper.querySelectorAll('[data-fieldset-index]')
  ).map((fieldset) => {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    return checked?.dataset.optionValueId;
  }).filter(Boolean);

  if (!selectedOptionValueIds.length) return;

  const url = `${productUrl}?option_values=${selectedOptionValueIds.join(',')}&sections=${sectionId}`;

  abortController?.abort();
  const ctrl = new AbortController();
  setAbortController(ctrl);

  fetch(url, { signal: ctrl.signal })
    .then((r) => r.json())
    .then((data) => {
      const sectionHtml = data[sectionId];
      if (!sectionHtml) return;

      const parser = new DOMParser();
      const newDoc = parser.parseFromString(sectionHtml, 'text/html');
      const newWrapper = newDoc.getElementById('fcb-product-options-size');
      if (!newWrapper) return;

      const currentWrapper = document.getElementById('fcb-product-options-size');
      if (currentWrapper) {
        const selectedValues = Array.from(currentWrapper.querySelectorAll('input[type="radio"]:checked'))
          .map((r) => ({ name: r.name, value: r.value }));

        currentWrapper.replaceWith(newWrapper);

        selectedValues.forEach(({ name, value }) => {
          const radio = newWrapper.querySelector(`input[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
          if (radio) radio.checked = true;
        });

        setupSizeSelectors();
        document.dispatchEvent(new CustomEvent('kit:section:refreshed', { bubbles: true }));
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        console.error('[kit] Failed to refresh section:', err);
      }
    });
}

// Customization style selector, rendered by kit-serigraphy-selector for kits
// without competition that list several serigraphies. Picking a style repaints
// the canvas with the whole serigraphy, like a competition change does, but
// leaves the fictitious product alone: it does not depend on the style.
function setupSerigraphySelectors() {
  const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
    document.querySelectorAll(".kit-serigraphy-radio")
  );
  if (!radios.length) return;

  const selectRadio = (/** @type {HTMLInputElement} */ radio) => {
    radio.checked = true;
    document.querySelectorAll(".kit-serigraphy-label").forEach((label) => {
      label.classList.toggle("selected", label.contains(radio));
    });
  };

  // Canvas text does not trigger the browser's lazy @font-face load, so every
  // style's font is fetched upfront and a style is painted once its font is in.
  /** @type {Map<string, Promise<void>>} */
  const fontLoads = new Map();

  radios.forEach((radio) => {
    const font = radio.dataset.serigraphyFont;
    if (font && !fontLoads.has(font)) fontLoads.set(font, preloadFont(font));

    radio.addEventListener("change", () => {
      selectRadio(radio);
      (fontLoads.get(font || "") ?? Promise.resolve()).then(() =>
        product?.updateCanvas(),
      );
    });
    radio.addEventListener("click", () => scrollToCustomizationImage());
  });

  // A ?serigraphy= GID that matches one of the styles starts selected, so the
  // selector agrees with what the URL paints.
  const urlRadio = Array.from(radios).find(
    (radio) => serigraphyParam && radio.dataset.serigraphyGid === serigraphyParam,
  );
  if (urlRadio) selectRadio(urlRadio);
}

function setupBadgeSelectors() {
  const radios = document.querySelectorAll(".kit-competition-radio");
  const labels = document.querySelectorAll(".kit-competition-label");

  radios.forEach((radio) => {
    radio.addEventListener("change", () => {
      const badgeTitle = radio.value;
      const variantId = radio.getAttribute("badge-id");
      const isReturnable = radio.dataset.isreturnable === "true";

      // Multi-pool: re-render sizes for the newly selected competition
      if (isMultiPoolMode && product) {
        console.log(`[Kit Pool] Competition changed to "${badgeTitle}" — rebuilding size selector`);
        const competitionHandle = getSelectedCompetitionHandle();

        setDefaultBaseProduct(pdpProductPool, competitionHandle);

        const currentSizeTitle = product.customization.size ?? null;
        const mergedVariants = buildMergedVariantsForCompetition(pdpProductPool, competitionHandle);
        renderSizeSelectorWithFallback(mergedVariants, currentSizeTitle);

        // Re-resolve variant from preserved radio state
        const newVariantId = resolveVariantFromOptions();
        if (newVariantId) {
          product.variantID = newVariantId;
          product.customization.size = product.getSizeLabelFromId(newVariantId) ?? product.customization.size;
        } else {
          product.variantID = null;
          product.customization.size = null;
        }

        // Sync currentBaseProductId from the checked size radio
        // (may differ from default if a size was preserved from another product)
        const sizeWrapper = document.getElementById('fcb-product-options-size');
        const checkedSizeRadio = /** @type {HTMLInputElement | null} */ (
          sizeWrapper?.querySelector('[data-fieldset-index] input[type="radio"]:checked')
        );
        if (checkedSizeRadio?.dataset.baseProductId) {
          product.currentBaseProductId = checkedSizeRadio.dataset.baseProductId;
          console.log(`[Kit Pool] After competition change — currentBaseProductId: ${product.currentBaseProductId}`);
        }

        // The resolved variant may have changed (different pool product or cleared size)
        syncNameLetterLimit();
      }

      if (product) {
        product.updateCompetition({
          title: badgeTitle,
          variantId,
          isReturnable,
        });
      }

      labels.forEach((label) => label.classList.remove("selected"));
      radio.parentElement.classList.add("selected");
    });
  });
}

function removeDuplicates(array) {
  const seen = new Set();
  return array.filter((item) => {
    const serialized = JSON.stringify(item);
    if (seen.has(serialized)) {
      return false;
    }
    seen.add(serialized);
    return true;
  });
}

document.getElementById("kit-player")?.addEventListener("change", () => {
  const player = extractSelectedPlayerData();
  if (player && product) {
    product.updatePlayer(
      player.name,
      player.number,
      player.reference,
      player.season,
      player.backID,
    );
    scrollToCustomizationImage();
    const badgeTitle = product.customization?.badge?.title ?? null;
    if (badgeTitle) {
      const competitionOption = document.querySelector(
        `input.kit-competition-radio[name="competition"][value="${badgeTitle}"]`,
      );
      const select = document.getElementById("kit-player");
      const currentValue = select?.value;
      updatePlayerDisplayOptions(competitionOption.dataset.gender);
      if (currentValue && select) {
        select.value = currentValue;
        if (select.value !== currentValue) select.selectedIndex = 0;
      }
    }
  }
});

function setupInputListener(selector, callback) {
  const input = document.querySelector(selector);
  if (input) {
    input.addEventListener("input", () => callback(input.value));
  }
}

function isEmpty(value) {
  if (value == null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  )
    return true;

  return false;
}

function isActiveCustomizationImage() {
  return document
    .getElementById("kit-customization-thumbnail")
    .className.includes("is-active");
}

setupInputListener("#kit-name", (value) => {
  const input = document.querySelector("#kit-name");

  if (containsBlacklistedWord(value)) {
    input.setCustomValidity("This name contains prohibited words.");
    input.reportValidity();

    setTimeout(() => {
      input.value = "";
      input.setCustomValidity("");

      product?.updatePlayerName("");
      product?.updateCustomizationType();
      product?.updateVariantID().then(() => product.updateCanvas());
    }, 100);

    return;
  } else {
    input.setCustomValidity("");
  }

  product?.updatePlayerName(value);
  if (!hasTriggeredUpdateForName && value.trim()) {
    hasTriggeredUpdateForName = true;
  } else if (
    hasTriggeredUpdateForName &&
    isEmpty(value) &&
    isEmpty(product.customization.playerNumber)
  ) {
    hasTriggeredUpdateForName = false;
    product?.updateCustomizationType();
  }

  if (!isActiveCustomizationImage()) {
    scrollToCustomizationImage();
  }

  product?.updateVariantID().then(() => product?.updateCanvas());
});

setupInputListener("#kit-number", (value) => {
  const typeBefore = product?.customization.type;
  product?.updatePlayerNumber(value);
  shortsLog('CUSTOM', `number typed "${value}"`, {
    customTabActive: document.querySelector('.kit-customization-tab[data-tab="custom"]')?.classList.contains('active') ?? '(no tab)',
    playerTabActive: document.querySelector('.kit-customization-tab[data-tab="player"]')?.classList.contains('active') ?? '(no tab)',
    typeBefore,
    typeAfter: product?.customization.type,
    kitTypeId: window.kit_type?.[product?.customization.type] ?? '⚠️ undefined → kit_type filter skipped',
  });
  if (!hasTriggeredUpdateForNumber && value.trim()) {
    hasTriggeredUpdateForNumber = true;
  } else if (
    hasTriggeredUpdateForNumber &&
    isEmpty(value) &&
    isEmpty(product.customization.playerName)
  ) {
    hasTriggeredUpdateForNumber = false;
    product?.updateCustomizationType();
  }

  if (!isActiveCustomizationImage()) {
    scrollToCustomizationImage();
  }

  product?.updateVariantID().then(() => product?.updateCanvas());
});

document.getElementById("kit-number")?.addEventListener("input", function () {
  if (product) {
    product.updatePlayerNumber(this.value);
  }
});

document
  .getElementById("kitAddToCartButton")
  ?.addEventListener("click", function (e) {
    e.preventDefault();

    product.submitButton = /** @type {HTMLButtonElement} */ (this);
    this.setAttribute("aria-disabled", true);
    this.classList.add("loading");
    this.querySelector(".loading-overlay__spinner")?.classList.remove("hidden");
    product.addToCartSubmission();
  });

async function initializeProductPage() {
  // Fetch and apply custom serigraphy from query parameter if present
  if (serigraphyParam) {
    customSerigraphyOptions = await fetchSerigraphyMetaobject(serigraphyParam);

    // Preload font if available
    if (customSerigraphyOptions?.text_font) {
      preloadFont(customSerigraphyOptions.text_font);
    }
  }

  // Preload the product's default serigraphy font. Unlike FCB-Play and the
  // FCBARCELONA fonts (used across the DOM / preloaded in fonts.liquid), the
  // default serigraphy font (e.g. FCB-Kobe-Away, FCB-Modernista-Home) is only
  // ever used on the canvas, and drawing canvas text does not trigger the
  // browser's lazy @font-face load. Without this, the first render falls back
  // to a system serif. Re-render once the font is registered in document.fonts.
  const defaultSerigraphy =
    readSelectedSerigraphy() ??
    readProductDefaultSerigraphy("kit-customization-canvas");
  const defaultSerigraphyFont = defaultSerigraphy?.text_font || "FCB-Modernista-Home";
  preloadFont(defaultSerigraphyFont).then(() => {
    if (product && !hasUrlCustomization) product.updateCanvas();
  });


  updateBadgePrice();
  if (product) {
    // Skip initial canvas render if URL customizations are present
    // to prevent firing event with wrong/default values
    if (!hasUrlCustomization) {
      product.updateCanvas();
    }
    if (!!product.customization.initialType) {
      updatePrices();
      updateBadgePrice();
    }
  }

  // Multi-pool: initialize pool-based competition and size selectors before binding listeners
  if (isMultiPoolMode) {
    console.log('[Kit Pool] initializeProductPage — running initial refreshProductContext');
    refreshProductContext(null);
  }

  setupSizeSelectors();

  const sizeWrapper = document.getElementById('fcb-product-options-size');
  if (!sizeWrapper && product) {
    const singleJsonScript = document.getElementById('kit-base-info-single-variant');
    if (singleJsonScript) {
      try {
        const singleVariant = JSON.parse(singleJsonScript.textContent);
        if (singleVariant?.id) {
          const variantId = String(singleVariant.id);
          if (product.customization.initialType) {
            product.selectSize(variantId);
          } else {
            product.variantID = variantId;
            product.customization.size = singleVariant.title || variantId;
          }
        }
      } catch (err) {
        console.error('[kit] Failed to parse single-variant JSON:', err);
      }
    }
  }

  // Only setup badges if NOT in no-competition mode
  if (!isNoCompetitionMode) {
    setupBadgeSelectors();

    const defaultCompetitionRadio = /** @type {HTMLInputElement | null} */ (document.querySelector(
      ".kit-competition-label.selected .kit-competition-radio",
    ));
    if (defaultCompetitionRadio && !defaultCompetitionRadio.checked) {
      defaultCompetitionRadio.checked = true;
      defaultCompetitionRadio.dispatchEvent(
        new Event("change", { bubbles: true }),
      );
    }

    updatePlayerDisplayOptions(defaultCompetitionRadio?.dataset.gender);
  } else {
    // No-competition mode: hide competition section
    const competitionSection = document.querySelector(
      ".kit-customization-competition",
    );
    if (competitionSection) {
      competitionSection.style.display = "none";
    }
  }

  if ("competition-player-fictitious" === product.customization.type) {
    scrollToCustomizationImage(true);
  }

  // Apply URL customizations after initialization
  // Reduced delay since serigraphy is already awaited
  setTimeout(() => {
    applyUrlCustomizations();
  }, 100);
}
// Function to apply competition from URL parameter
function applyCompetitionFromUrl(competitionValue) {
  if (!competitionValue || competitionValue === "no-competition") {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const radio = document.querySelector(
      `input.kit-competition-radio[value="${competitionValue}"]`,
    );

    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));

      document.querySelectorAll(".kit-competition-label").forEach((label) => {
        label.classList.remove("selected");
      });
      radio.parentElement.classList.add("selected");

      resolve();
    } else {
      console.warn(`[Kit] Competition "${competitionValue}" not found`);
      const event = new CustomEvent("kit:competition-not-found", {
        detail: { competition: competitionValue },
      });
      document.dispatchEvent(event);
      resolve();
    }
  });
}

// Function to apply name from URL parameter
function applyNameFromUrl(name) {
  if (!name) {
    return;
  }

  const customTab = document.querySelector(
    '.kit-customization-tab[data-tab="custom"]',
  );
  if (customTab && !customTab.classList.contains("active")) {
    handleTabClick(customTab);
  }

  // Set the name input
  const nameInput = document.querySelector("#kit-name");
  if (nameInput) {
    // maxlength does not apply to values set by script, so enforce the current letter limit here
    nameInput.value = nameInput.maxLength > 0 ? name.slice(0, nameInput.maxLength) : name;
    nameInput.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

// Function to apply number from URL parameter
function applyNumberFromUrl(number) {
  if (!number) {
    return;
  }

  const customTab = document.querySelector(
    '.kit-customization-tab[data-tab="custom"]',
  );
  if (customTab && !customTab.classList.contains("active")) {
    handleTabClick(customTab);
  }

  const numberInput = document.querySelector("#kit-number");
  if (numberInput) {
    numberInput.value = number;
    numberInput.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

// Function to apply all URL customizations
async function applyUrlCustomizations() {
  if (!hasUrlCustomization) {
    return;
  }

  // Apply competition
  if (competitionParam && competitionParam !== "no-competition") {
    await applyCompetitionFromUrl(competitionParam);
  }

  if (nameParam || numberParam) {
    setTimeout(() => {
      if (nameParam) applyNameFromUrl(nameParam);
      if (numberParam) applyNumberFromUrl(numberParam);
    }, 200);
  }
}

window.addEventListener("load", initializeProductPage);

window.addEventListener("resize", () => {
  if (product) product.updateCanvas();
});

// Descarga del render (imagen base + serigrafía + badges) a 1400px en WebP.
function setupRenderDownload() {
  const button = document.getElementById("kit-render-download");
  if (!button) return;

  function buildFileName() {
    const parts = [
      product?.handle || product?.title || "render",
      product?.customization?.playerName,
      product?.customization?.playerNumber,
    ].filter(Boolean);

    return (
      parts
        .join("-")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "render"
    );
  }

  button.addEventListener("click", async (event) => {
    event.stopPropagation();

    if (!product?.canvas || typeof product.canvas.exportRenderBlob !== "function") {
      console.warn("Render no disponible para descargar");
      return;
    }
    if (button.getAttribute("aria-busy") === "true") return;

    button.setAttribute("aria-busy", "true");

    try {
      // El modal reasigna product.canvas; re-renderizamos el canvas principal
      // de la PDP para asegurar que exportamos la slide de personalización.
      product.updateCanvas();

      const blob = await product.canvas.exportRenderBlob({
        width: 1400,
        type: "image/webp",
        quality: 0.92,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${buildFileName()}.webp`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error descargando el render:", error);
    } finally {
      button.removeAttribute("aria-busy");
    }
  });
}

document.addEventListener("DOMContentLoaded", setupRenderDownload);
